import React, { useState, useEffect, useContext, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Axios from 'axios';
import { Loader } from 'components/lib';
import { AuthContext, ViewContext, Event } from 'components/lib';
import {
  Avatar,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Button,
  Autocomplete,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Alert,
  useMediaQuery, 
  useTheme,
  Grid,
  Chip,
  Stack,
  IconButton,
  Tooltip
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { DeleteOutline as DeleteIcon, 
          DifferenceOutlined as CloneIcon, 
          CloseOutlined as CloseIcon,
          EditOutlined as EditIcon,
          RemoveCircleOutline as RemoveIcon,
          UndoOutlined as UndoIcon,
          InfoOutlined as InfoIcon,
          HelpOutlineOutlined as QuestionIcon,
          SubjectOutlined as CodeIcon,
          ContentCopy as CopyIcon,
          UnfoldMoreOutlined as SkewIcon
} from '@mui/icons-material';
import { indigo, green, yellow, grey } from '@mui/material/colors';
import { LineChart } from '@mui/x-charts/LineChart';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { solarizedDarkAtom } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Text } from 'mbd-components/text';
import { PrimaryButton } from 'mbd-components/primary-button';
import { SecondaryButton } from 'mbd-components/secondary-button';
import { BrandedDialog } from 'mbd-components/branded-dialog';
import { Slider } from 'mbd-components/slider';
import { Card } from 'mbd-components/card';
import { EndpointTag } from 'mbd-components/endpoint-tag';
import { CountField } from './count_field';
import { MinMaxInput } from './min_max_input';
import { EditableTextField } from './editable_text_field';
import { FeedSelector } from './feed_selector';
import { MetricBox } from './metric_box';
import { FeedTemplateShowcase } from './feedTemplateShowcase';
import { aiLabelsOptions, tbaAiLabelCategoriesOptions, tbaAiLabelTopicsOptions, embedDomainsOptions, endpointOptions, publicationTypesOptions, appFidOptions, scoringOptions, videoOrientationOptions, videoLanguageOptions, miniappCategoriesOptions } from 'helper/constants';
import { geoLocations } from 'helper/geo_locations';
import { colors } from 'helper/colors';
import { getLocationName, getGeloLocationDisplayString } from 'helper/utils';
import { languages } from 'helper/languages';
import { explanations } from 'helper/explanations';
import { getCodeString } from 'helper/utils';
import logo_image from '../../assets/logo_image.png';

const customStyle = {
  borderRadius: '5px',
  backgroundColor: '#002b36',
  color: '#93a1a1',
  padding: '15px',
  margin: '10px 0',
  fontSize: '13px',
  width: '100%',
  height: 'auto',
  textAlign: 'left', // Align text to the left
  alignItems: 'left',
  overflowX: 'auto',
  '@media (max-width: 768px)': {
    fontSize: '11px',
    padding: '12px',
  },
  '@media (max-width: 480px)': {
    fontSize: '10px',
    padding: '10px',
  },
};

const primaryColor = '#e01296';

const DEFAULT_TIME_PERIOD = 30;
const METRIC_VALID_START_DATE = '2025-06-20';
const MAX_FEED_PROMOTIONS = 10;


export function FeedConfig() {
  const [visibility, setVisibility] = useState('private');
  const [isEditMode, setIsEditMode] = useState(false);
  const [feedId, setFeedId] = useState('');
  const [feedName, setFeedName] = useState('');
  const [description, setDescription] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [status, setStatus] = useState('active');
  const [scoring, setScoring] = useState(scoringOptions[0]);
  const [impressionCount, setImpressionCount] = useState(0);
  const [timePeriod, setTimePeriod] = useState(DEFAULT_TIME_PERIOD);
  const [timePeriodUnit, setTimePeriodUnit] = useState('days');
  const [showEngagementFilters, setShowEngagementFilters] = useState(false);
  const [showAiLabelThresholds, setShowAiLabelThresholds] = useState(false);
  const [minLikeCount, setMinLikeCount] = useState('');
  const [maxLikeCount, setMaxLikeCount] = useState('');
  const [minCommentCount, setMinCommentCount] = useState('');
  const [maxCommentCount, setMaxCommentCount] = useState('');
  const [minShareCount, setMinShareCount] = useState('');
  const [maxShareCount, setMaxShareCount] = useState('');
  const [promotionTypeValue, setPromotionTypeValue] = useState('feed');
  const [appFids, setAppFids] = useState([]);
  const [selectedGeoLocations, setSelectedGeoLocations] = useState([]);
  const [excludeGeoLocations, setExcludeGeoLocations] = useState([]);
  const [includesAILABELS, setIncludeAILABELS] = useState([]);
  const [excludeAILABELS, setExcludeAILABELS] = useState([]);
  const [includeAuthorIds, setIncludeAuthorIds] = useState([]);
  const [excludeAuthorIds, setExcludeAuthorIds] = useState([]);
  const [returnEngagedByAuthorIds, setReturnEngagedByAuthorIds] = useState(false);
  const [channels, setChannels] = useState([]);
  const [excludeChannels, setExcludeChannels] = useState([]);
  const [embedDomains, setEmbedDomains] = useState([]);
  const [includePublicationTypes, setIncludePublicationTypes] = useState([]);
  const [includeLanguages, setIncludeLanguages] = useState([]);
  const [includeParams, setIncludeParams] = useState([]);
  const [excludeParams, setExcludeParams] = useState([]);
  const [openIncludeDialog, setOpenIncludeDialog] = useState(false);
  const [openExcludeDialog, setOpenExcludeDialog] = useState(false);
  const [openContextualInfoDialog, setOpenContextualInfoDialog] = useState(false);
  const [openLabelOptionsDialog, setOpenLabelOptionsDialog] = useState(false);
  const [openExcludeLabelOptionsDialog, setOpenExcludeLabelOptionsDialog] = useState(false);
  const [selectedIncludeParams, setSelectedIncludeParams] = useState([]);
  const [selectedExcludeParams, setSelectedExcludeParams] = useState([]);
  const [selectedContextualInfoParams, setSelectedContextualInfoParams] = useState([]);
  const [contextualAppFid, setContextualAppFid] = useState([]);
  const [selectedContextualGeoLocation, setSelectedContextualGeoLocation] = useState([]);
  const [publicFeeds, setPublicFeeds] = useState([]); // State for public feeds
  const [privateFeeds, setPrivateFeeds] = useState([]); // State for private feeds
  const [isNewFeed, setIsNewFeed] = useState(false);
  const [authorOptions, setAuthorOptions] = useState([]);
  const [channelOptions, setChannelOptions] = useState([]);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [savedConfigId, setSavedConfigId] = useState('');
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [showSaveAs, setShowSaveAs] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isMetricsLoading, setIsMetricsLoading] = useState(false);
  const [promotionName, setPromotionName] = useState('');
  const [selectedPromotionFeedId, setSelectedPromotionFeedId] = useState('');
  const [selectedPromotionFeedName, setSelectedPromotionFeedName] = useState('');
  const [promotionPercentage, setPromotionPercentage] = useState(20);
  const [promotionEditIndex, setPromotionEditIndex] = useState(-1);
  const [aiLabelThresholds, setAiLabelThresholds] = useState({});
  const [removeAILabelThresholds, setRemoveAILabelThresholds] = useState({});
  const [showPromotionSection, setShowPromotionSection] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(true);
  const [itemsList, setItemsList] = useState([]);
  const [currentAiLabel, setCurrentAiLabel] = useState('');
  const [currentAiLabelThreshold, setCurrentAiLabelThreshold] = useState(0);
  const [isShowcaseMode, setIsShowcaseMode] = useState(false);
  const [iframeKey, setIframeKey] = useState(Date.now());
  const [showInteracted, setShowInteracted] = useState(false);
  const [removePostsFromCurrentUser, setRemovePostsFromCurrentUser] = useState(false);
  const [withSocialProofOnly, setWithSocialProofOnly] = useState(false);
  const [showFallbackSection, setShowFallbackSection] = useState(false);
  const [fallbackFeedId, setFallbackFeedId] = useState('');
  const [fallbackFeedName, setFallbackFeedName] = useState('');
  const [fallbackListOptions, setFallbackListOptions] = useState([]);
  const [fallbackFeedList, setFallbackFeedList] = useState([]);
  const [coldStartFeedId, setColdStartFeedId] = useState('');
  const [coldStartFeedName, setColdStartFeedName] = useState('');
  const [showColdStartSection, setShowColdStartSection] = useState(false);
  const [coldStartThreshold, setColdStartThreshold] = useState('');
  const [coldStartMinimumInteractionCount, setColdStartMinimumInteractionCount] = useState('');
  const [geoLocationWarningVisible, setGeoLocationWarningVisible] = useState(false);
  const [authorIdWarningVisible, setAuthorIdWarningVisible] = useState(false);
  const [dontReturnGlobalFallback, setDontReturnGlobalFallback] = useState(false);
  const [useTimeDecay, setUseTimeDecay] = useState(false);
  const [timeDecayValue, setTimeDecayValue] = useState(0);
  const [showFeedDiversitySection, setShowFeedDiversitySection] = useState(false);
  const [maxPostsPerAuthor, setMaxPostsPerAuthor] = useState(0);
  const [minDistanceBetweenPostsFromSameAuthor, setMinDistanceBetweenPostsFromSameAuthor] = useState(0);
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [infoDialogTitle, setInfoDialogTitle] = useState('');
  const [infoDialogText, setInfoDialogText] = useState([]);
  const [metricDates, setMetricDates] = useState([]);
  const [dauDates, setDauDates] = useState([]);
  const [dauDataset, setDauDataset] = useState([]);
  const [dauAverage, setDauAverage] = useState(0);
  const [impressionsDates, setImpressionsDates] = useState([]); 
  const [impressionsDataset, setImpressionsDataset] = useState([]);
  const [impressionsAverage, setImpressionsAverage] = useState(0);
  const [dailyEngagementDates, setDailyEngagementDates] = useState([]);
  const [dailyEngagementDataset, setDailyEngagementDataset] = useState([]);
  const [dailyEngagementAverage, setDailyEngagementAverage] = useState(0);
  const [uniqueAuthorsDates, setUniqueAuthorsDates] = useState([]);
  const [uniqueAuthorsDataset, setUniqueAuthorsDataset] = useState([]);
  const [uniqueAuthorsAverage, setUniqueAuthorsAverage] = useState(0);
  const [uniquePostsDates, setUniquePostsDates] = useState([]);
  const [uniquePostsDataset, setUniquePostsDataset] = useState([]);
  const [uniquePostsAverage, setUniquePostsAverage] = useState(0);
  const [engagedUsersDates, setEngagedUsersDates] = useState([]);
  const [engagedUsersDataset, setEngagedUsersDataset] = useState([]);
  const [engagedUsersAverage, setEngagedUsersAverage] = useState(0);
  const [codeString, setCodeString] = useState('');
  const [showCodeDialog, setShowCodeDialog] = useState(false);
  const [includeVideoOrientations, setIncludeVideoOrientations] = useState([]);
  const [includeMiniappCategories, setIncludeMiniappCategories] = useState([]);
  const [includeVideoLanguages, setIncludeVideoLanguages] = useState([]);
  const [minVideoDuration, setMinVideoDuration] = useState(0);
  const [maxVideoDuration, setMaxVideoDuration] = useState(0);
  const [showPromotionDialog, setShowPromotionDialog] = useState(false);
  const [promotionList, setPromotionList] = useState([]);
  const [showAddPromotionSection, setShowAddPromotionSection] = useState(false);
  const [skew, setSkew] = useState(0);
  const [invalidPercentage, setInvalidPercentage] = useState(false);
  const [dynamicAILabelDialogOpen, setDynamicAILabelDialogOpen] = useState(false);
  const [groupedAILabelOptions, setGroupedAILabelOptions] = useState([]);
  const [thresholdDialogOpen, setThresholdDialogOpen] = useState(false);
  const [customAiLabelThresholds, setCustomAiLabelThresholds] = useState([]);

  const [showTbaOptions, setShowTbaOptions] = useState(false);


  const playgroundUrl = process.env.REACT_APP_PLAYGROUND_URL;

  
  const location = useLocation();
  const { feedId: initialFeedId, mode, new: initialNewFeed } = location.state || {};
  const promotionRef = useRef(null);

  const promotionTypeList = [
    { value: 'feed', label: 'Feed' },
    { value: 'items', label: 'Items' }
  ]

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const togglePreviewDrawer = () => {
    setIsPreviewOpen(!isPreviewOpen);
  };


  const authContext = useContext(AuthContext);
  const viewContext = useContext(ViewContext);

  const statusOptions = ['active', 'inactive'];

  // Fetch feeds based on visibility type
  const fetchFeeds = async (type) => {
    try {
      let result = await Axios({
        url: '/api/feed/configs',
        method: 'post',
        data: {
          visibility: type,
        },
      });
      return result?.data?.data || [];
    } catch (error) {
      console.error(`Error fetching ${type} feeds:`, error);
      setSnackbarMessage(`Error fetching ${type} feeds`);
      setShowSnackbar(true);
      return [];
    }
  };

  const fetchFeedMetrics = async (feedId) => {

    if(feedId === ''){
      setIsMetricsLoading(false);
      return;
    }

    try {
      setIsMetricsLoading(true);
      let result = await Axios({
        url: '/api/stats/feed/' + feedId,
        method: 'get'
      });
      if (result?.data?.data.length > 0) {
        let metricDates = [];
        let dauDataset = [];
        let impressionsDataset = [];
        let dailyEngagementDataset = [];
        let uniqueAuthorsDataset = [];
        let uniquePostsDataset = [];
        for (let i = 0; i < result?.data?.data.length; i++) {
          const metric = result?.data?.data[i];
          if(metric.date){
            metricDates.push(metric.date);
            dauDataset.push(metric.dau || 0);
            impressionsDataset.push(metric.impressions || 0);
            dailyEngagementDataset.push(metric.daily_engagement || 0);
            uniqueAuthorsDataset.push(metric.unique_authors || 0);
            uniquePostsDataset.push(metric.unique_items || 0);
          }
        }
        setMetricDates(metricDates); 
        setDauDataset(dauDataset);
        setImpressionsDataset(impressionsDataset);
        setDailyEngagementDataset(dailyEngagementDataset);
        setUniqueAuthorsDataset(uniqueAuthorsDataset);
        setUniquePostsDataset(uniquePostsDataset);
      }
      setIsMetricsLoading(false);
      return result?.data?.data || [];
    } catch (error) {
      console.error(`Error fetching metrics for feed ${feedId}:`, error);
      setSnackbarMessage(`Error fetching feed metrics`);
      setShowSnackbar(true);
      setIsMetricsLoading(false);
      return [];
    }
  }

  const extractMetricData = (data) => {
    let dates = [];
    let dataPoints = [];
    let validDatePointCount = 0;
    let total = 0;
    for (let i = 0; i < data.length; i++) {
      if(data[i].date){
        dates.push(data[i].date);
        if (new Date(data[i].date) >= new Date(METRIC_VALID_START_DATE)){
          dataPoints.push(data[i].value || 0);
          validDatePointCount++;
          total += data[i].value || 0;
        }
        else {
          dataPoints.push(0);
        }
      }
    }
    return {dates, dataPoints, average:total / validDatePointCount};
  }

  const fetchMetricDataset = async (feedId, metricName) => {
    // const mockDauDataset = Array.from({ length: 30 }, () => Math.floor(Math.random() * (2000 - 500 + 1)) + 500);

    let result = await Axios({
      url: '/api/stats/feed/' + feedId + '/' + metricName,
      method: 'get'
    });
    return result?.data?.data?.data || [];
  }
  const fetchDauDataset = async (feedId) => {
    const dauData = await fetchMetricDataset(feedId, 'dau');
    const {dates, dataPoints, average} = extractMetricData(dauData);
    setDauAverage(Math.round(average));
    setDauDates(dates);
    setDauDataset(dataPoints);
  }

  const fetchImpressionsDataset = async (feedId) => {
    const impressionsData = await fetchMetricDataset(feedId, 'impressions');
    const {dates, dataPoints, average} = extractMetricData(impressionsData);
    setImpressionsAverage(Math.round(average));
    setImpressionsDates(dates);
    setImpressionsDataset(dataPoints);
  }

  const fetchEngagementDataset = async (feedId) => {
    const engagementData = await fetchMetricDataset(feedId, 'engaged_items');
    const {dates, dataPoints, average} = extractMetricData(engagementData);
    setDailyEngagementAverage(Math.round(average));
    setDailyEngagementDates(dates);
    setDailyEngagementDataset(dataPoints);
  }

  const fetchUniqueAuthorsDataset = async (feedId) => {
    const uniqueAuthorsData = await fetchMetricDataset(feedId, 'unique_authors');
    const {dates, dataPoints, average} = extractMetricData(uniqueAuthorsData);
    setUniqueAuthorsAverage(Math.round(average));
    setUniqueAuthorsDates(dates);
    setUniqueAuthorsDataset(dataPoints);
  }

  const fetchUniquePostsDataset = async (feedId) => {
    const uniquePostsData = await fetchMetricDataset(feedId, 'unique_items');
    const {dates, dataPoints, average} = extractMetricData(uniquePostsData);
    setUniquePostsAverage(Math.round(average));
    setUniquePostsDates(dates);
    setUniquePostsDataset(dataPoints);
  }

  const fetchEngagedUsersDataset = async (feedId) => {
    const engagedUsersData = await fetchMetricDataset(feedId, 'engaged_users');
    const {dates, dataPoints, average} = extractMetricData(engagedUsersData);
    setEngagedUsersAverage(Math.round(average));
    setEngagedUsersDates(dates);
    setEngagedUsersDataset(dataPoints);
  }
  
  const fetchAllFeedMetrics = async (feedId) => {
    setIsMetricsLoading(true);
    fetchDauDataset(feedId);
    fetchImpressionsDataset(feedId);
    fetchEngagementDataset(feedId);
    fetchUniqueAuthorsDataset(feedId);
    fetchUniquePostsDataset(feedId);
    fetchEngagedUsersDataset(feedId);
    setIsMetricsLoading(false);
  }

  useEffect(() => {
    console.log(authContext.user);
    if (authContext.user?.plan === 'enterprise_coinbase'){
      setShowTbaOptions(true);
      setGroupedAILabelOptions(
        aiLabelsOptions.map(option => ({set: 'mbd AI Labels', label: option, value: option})).concat(
          tbaAiLabelCategoriesOptions.map(option => ({set: 'TBA AI Labels', label: 'tba_c_' +option, value: 'tba_c_' +option})).concat(
            tbaAiLabelTopicsOptions.map(option => ({set: 'TBA AI Labels', label: 'tba_t_' +option, value: 'tba_t_' +option}))
          )
        )
      );
    }
    else{
      setGroupedAILabelOptions(
        aiLabelsOptions.map(option => ({set: 'mbd AI Labels', label: option, value: option}))
      );
    }
  }, [authContext.user?.plan]);

  useEffect(() => {
    if(showTbaOptions){
      setGroupedAILabelOptions(
        aiLabelsOptions.map(option => ({set: 'mbd AI Labels', label: option, value: option})).concat(
          tbaAiLabelCategoriesOptions.map(option => ({set: 'TBA AI Labels', label: 'tba_c_' +option, value: 'tba_c_' +option})).concat(
            tbaAiLabelTopicsOptions.map(option => ({set: 'TBA AI Labels', label: 'tba_t_' +option, value: 'tba_t_' +option}))
          )
        )
      );
    }
    else{
      setGroupedAILabelOptions(
        aiLabelsOptions.map(option => ({set: 'mbd AI Labels', label: option, value: option}))
      );
    }
  }, [showTbaOptions]);

  useEffect(() => {
    // reset chart data
    setMetricDates([]); 
    setDauDataset([]);
    setImpressionsDataset([]);
    setDailyEngagementDataset([]);
    setUniqueAuthorsDataset([]);
    setUniquePostsDataset([]);
    setEngagedUsersDataset([]);

    setDauDates([]);
    setImpressionsDates([]);
    setDailyEngagementDates([]);
    setUniqueAuthorsDates([]);
    setUniquePostsDates([]);
    setEngagedUsersDates([]);

    // trigger fetch of metrics
    if(feedId !== ''){
      fetchAllFeedMetrics(feedId);
    }
  }, [feedId]);

  useEffect(() => {
    const currentTotalPromotionPercentage = promotionList.reduce((acc, promotion) => acc + promotion.percent, 0);
    if(currentTotalPromotionPercentage + promotionPercentage > 100){
      setInvalidPercentage(true);
    } else {
      setInvalidPercentage(false);
    }
  },[promotionPercentage, promotionList.length])

  useEffect(() => {
    if(showAddPromotionSection){
      promotionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [showAddPromotionSection]);
  

  const getCurrentFallbackListOptions = () => {
    return (privateFeeds.concat(publicFeeds).filter(
      (f) => f.config_id !== feedId && !fallbackFeedList.includes(f.config_id)
    ).map(
      (feed) => ({label: `${feed.name} (${feed.config_id})`, value: feed.config_id})
    ));
  }

  useEffect(() => {
    setFallbackListOptions(getCurrentFallbackListOptions());
  }, [endpoint, feedId, fallbackFeedList]);

  // Fetch both public and private feeds simultaneously
  const fetchAllFeeds = async () => {
    setLoading(true);
    try {
      const [publicFeedsData, privateFeedsData] = await Promise.all([
        fetchFeeds('template'),
        fetchFeeds('private'),
      ]);
      setPublicFeeds(publicFeedsData);
      setPrivateFeeds(privateFeedsData);
      setFallbackListOptions(getCurrentFallbackListOptions());

    } catch (error) {
      console.error('Error fetching all feeds:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch channels details based on channel ids
  const fetchChannelsDetails = async (channels) => {
    if (!channels || channels.length === 0) return [];
    try {
      let channelsString = channels.join(',');
      let result = await Axios({
        url: '/api/farcaster/channel/bulk',
        method: 'get',
        params: { ids: channelsString, type: 'parent_url' },
      });
      return result?.data?.data || [];
    } catch (error) {
      console.error('Error fetching channels details:', error);
      setSnackbarMessage('Error fetching channel details');
      setShowSnackbar(true);
      return [];
    }
  };

  // Fetch author details based on author ids
  const fetchAuthorDetails = async (authorIds) => {
    if (!authorIds || authorIds.length === 0) return [];
    let authorIdList = [];
    let nonAuthorIdList = [];
    let resultList = [];

    authorIds.filter((id) => {
      if(id === 'user/user_id/following'){
       resultList.push({fname: 'following', fid: 'user/user_id/following'});
      }
      else if(id.startsWith('user/')){
        let fidVal = id.split('/')[1];
        nonAuthorIdList.push(fidVal);
      }else{
        authorIdList.push(id);
      }
    });

    try {
      let result = await Axios({
        url: '/api/farcaster/users/details',
        method: 'post',
        data: { fids: authorIdList },
      });
      resultList = resultList.concat(result?.data?.data || []);

      result  = await Axios({
        url: '/api/farcaster/users/details',
        method: 'post',
        data: { fids: nonAuthorIdList },
      });

      result?.data?.data.map((item) => {
          resultList.push({fname: `${item.fname}:following`, fid: `user/${item.fid}/following`});
      });

      return resultList || [];
    } catch (error) {
      console.error('Error fetching author details:', error);
      setSnackbarMessage('Error fetching author details');
      setShowSnackbar(true);
      return [];
    }
  };

  // Fetch autocomplete options based on user input and type
  const fetchAutocompleteOptions = async (query, type) => {
    if (!query) return [];
    try {
      if (type === 'author_ids' || type === 'user_ids') {
        let result = await Axios({
          url: '/api/farcaster/users/search',
          method: 'get',
          params: { query: query },
        });
        const options = result?.data?.data.map((item) => ({ label: item.fname, value: item.fid })) || [];


        let resultOptions = [];
        options.forEach((item) => {
          resultOptions.push({ label: item.label, value: item.value });
          if (type === 'author_ids') {
            // Add ":following" option for each result
            resultOptions.push({ label: `${item.label}:following`, value: `user/${item.value}/following` });
          }
        });
        // const followingOptions = options.map((item) => (
        //   resultOptions.push({ label: item.label, value: item.value }),
        //   resultOptions.push({ label: `${item.label}:following`, value: `user/${item.value}/following` })
        // ));
  
        return resultOptions || [];
      }
      if (type === 'channels') {
        let result = await Axios({
          url: '/api/farcaster/channel/search',
          method: 'get',
          params: { q: query, limit: 20 },
        });
        return result?.data?.data?.map((item) => ({ label: item.label, value: item.value })) || [];
      }
      return [];
    } catch (error) {
      console.error(`Error fetching ${type} options:`, error);
      setSnackbarMessage(`Error fetching ${type} options`);
      setShowSnackbar(true);
      return [];
    }
  };

  const handleVisibilityChange = (event, newVisibility) => {
    if (newVisibility !== null && visibility !== newVisibility) {
      setVisibility(newVisibility);
      setFeedId(''); // Reset feed ID when changing visibility
      resetFormFields(); // Reset form fields when visibility changes
      const feedsToUse = newVisibility === 'public' ? publicFeeds : privateFeeds;
      // Auto-select the first feed in the new visibility state if available
      if (feedsToUse.length > 0) {
        const firstFeed = feedsToUse[0];
        setFeedId(firstFeed.config_id);
        populateFeedDetails(firstFeed);
      }

      if(newVisibility === 'public') {
        Event('feed_builder_explore_feeds');
        setShowSaveAs(true);
        setIsEditMode(false);
        setIsShowcaseMode(true);
      }
      else {
        Event('feed_builder_manage_my_feeds');
        setIsShowcaseMode(false);
      }
      // setIsPreviewOpen(true);
    }
  };

  const handleCreateNewFeed = () => {
    Event("feed_builder_create_new_feed");
    setIsNewFeed(true);
    resetFormFields();
    createNewFeed();
  }

  const handleFeedSelection = async (event) => {
    const selectedFeedId = event.target.value;
    if (selectedFeedId === 'new') {
      Event("feed_builder_create_new_feed");
      setIsNewFeed(true);
      resetFormFields();
    } else {
      Event("feed_builder_select_my_feed_from_dropdown");
      setIsNewFeed(false);
      setFeedId(selectedFeedId);
      const feeds = visibility === 'public' ? publicFeeds : privateFeeds;
      const selectedFeed = feeds.find((feed) => feed.config_id === selectedFeedId);
      if (selectedFeed) {
        populateFeedDetails(selectedFeed);
      }
      // setIsPreviewOpen(true);
    }
  };

  const resetFormFields = () => {
    setFeedId('');
    setFeedName('');
    setDescription('');
    setEndpoint('');
    setStatus('');
    setScoring(scoringOptions[0]);
    setAppFids([]);
    setSelectedGeoLocations([]);
    setExcludeGeoLocations([]);
    setIncludeAILABELS([]);
    setExcludeAILABELS([]);
    setIncludeAuthorIds([]);
    setExcludeAuthorIds([]);
    setIncludePublicationTypes([]);
    setIncludeLanguages([]);
    setChannels([]);
    setExcludeChannels([]);
    setEmbedDomains([]);
    setIncludeParams([]);
    setExcludeParams([]);
    setAuthorOptions([]);
    setChannelOptions([]);
    setPromotionName('');
    setSelectedPromotionFeedId('');
    setSelectedPromotionFeedName('');
    setSelectedExcludeParams([]);
    setSelectedIncludeParams([]);
    setPromotionPercentage(20);
    setItemsList([]);
    setShowPromotionSection(false);
    setShowColdStartSection(false);
    setShowInteracted(false);
    setColdStartFeedId(''); 
    setColdStartThreshold('');
    setColdStartMinimumInteractionCount('');
    setTimePeriod(DEFAULT_TIME_PERIOD);
    setDontReturnGlobalFallback(false);
    setShowFallbackSection(false);
    setFallbackFeedId('');
    setFallbackFeedName('');
    setUseTimeDecay(false);
    setTimeDecayValue(0);
    setShowFeedDiversitySection(false);
    setMaxPostsPerAuthor(0);
    setMinDistanceBetweenPostsFromSameAuthor(0);
    setShowEngagementFilters(false);
    setMinLikeCount('');
    setMaxLikeCount('');
    setMinCommentCount('');
    setMaxCommentCount('');
    setMinShareCount('');
    setMaxShareCount('');
    setWithSocialProofOnly(false);
    setRemovePostsFromCurrentUser(false);
    setIncludeVideoOrientations([]);
    setMinVideoDuration(0);
    setMaxVideoDuration(0);
    setIncludeVideoLanguages([]);
    setIncludeMiniappCategories([]);
    setInvalidPercentage(false);
    setPromotionList([]);
  };

  const populateFeedDetails = async (feed) => {
    setFeedName(feed.name);
    setDescription(feed.description);
    setEndpoint(feed.endpoint);
    setStatus(feed.status);
    setScoring(feed.config.scoring && scoringOptions.includes(feed.config.scoring) ? feed.config.scoring : scoringOptions[0]);
    setImpressionCount(feed.config.impression_count || 0);
    setUseTimeDecay(feed.config.hasOwnProperty('time_decay'));
    setTimeDecayValue(feed.config.time_decay || 0);
    if (feed.config.filters.start_timestamp) {
      if (feed.config.filters.start_timestamp.includes(':')) {
        setTimePeriod(parseInt(feed.config.filters.start_timestamp.split(':')[1]) || DEFAULT_TIME_PERIOD);
        setTimePeriodUnit(feed.config.filters.start_timestamp.split(':')[0] === 'days_ago' ? 'days' : 'hours');
      } else {
        setTimePeriod(parseInt(feed.config.filters.start_timestamp) || DEFAULT_TIME_PERIOD);
        setTimePeriodUnit('days');
      }
    }
    setShowInteracted(feed.config.show_interacted_items || false);
    setWithSocialProofOnly(feed.config.return_only_posts_with_social_proof || false);
    setRemovePostsFromCurrentUser(feed.config.remove_user_posts_from_feed || false);
    if (feed.config.hasOwnProperty('feed_diversity_config')) {
      setShowFeedDiversitySection(true);
      if (feed.config.feed_diversity_config.max_posts_per_author) {
        setMaxPostsPerAuthor(feed.config.feed_diversity_config.max_posts_per_author);
      }
      if (feed.config.feed_diversity_config.min_distance_between_posts_from_same_author) {
        setMinDistanceBetweenPostsFromSameAuthor(feed.config.feed_diversity_config.min_distance_between_posts_from_same_author);
      }
    }

    if(feed?.config?.cold_start?.cold_start_feed?.feed_id){
      setShowColdStartSection(true);
      setColdStartFeedId(feed.config.cold_start?.cold_start_feed?.feed_id || '');
    }else{
      setShowColdStartSection(false);
      setColdStartFeedId('');
    }

    if(feed?.config?.cold_start?.cold_start_threshold_days){
      setColdStartThreshold(feed.config.cold_start.cold_start_threshold_days);
    }
    else{
      setColdStartThreshold('');
    }
    if(feed?.config?.cold_start?.min_interactions_count){
      setColdStartMinimumInteractionCount(feed.config.cold_start.min_interactions_count);
    }
    else{
      setColdStartMinimumInteractionCount('');
    }

    if(feed?.config?.global_fallback?.global_fallback_feed?.feed_id){
      setShowFallbackSection(true);
      setFallbackFeedList([feed.config.global_fallback?.global_fallback_feed?.feed_id]);
      // setFallbackFeedId(feed.config.global_fallback?.global_fallback_feed?.feed_id || '');
    }else{
      setShowFallbackSection(false);
      setFallbackFeedId('');
    }

    if (feed?.config?.fallback_feeds && feed?.config?.fallback_feeds?.length > 0){
      setShowFallbackSection(true);
      setFallbackFeedList(feed.config.fallback_feeds.map((f) => f.feed_id));
    }

    // Initialize AI label thresholds
    let initialIncludeThresholds = {};
    let initialExcludeThresholds = {};

    // Populate promotion fields based on existing config
    if (feed.config.promotion_filters && feed.config.promotion_filters.length > 0) {
      const promotionList = feed.config.promotion_filters.filter((promotion) => promotion.promotion_type === 'feed');
      if(promotionList.length > 0){
        setPromotionList(promotionList);
      }
      else {
        setPromotionList([]);
      }
      const itemsPromotion = feed.config.promotion_filters.filter((promotion) => promotion.promotion_type === 'items');
      if(itemsPromotion.length > 0){
        setItemsList(itemsPromotion[0].promoted_items);
      }
      else {
        setItemsList([]);
      }
      // const promotion = feed.config.promotion_filters[0];
      // if(promotion.promotion_type === 'feed'){
      //   setPromotionTypeValue(promotion.promotion_type || '');
      //   setPromotionName(promotion.promotion_name || '');
      //   setSelectedPromotionFeedId(promotion.feed_id || '');
      //   setSelectedPromotionFeedName(
      //     [...publicFeeds, ...privateFeeds].find((f) => f.config_id === promotion.feed_id)?.name || ''
      //   );
      //   setPromotionPercentage(promotion.percent || 0);
      //   setItemsList([]);
      // }else if(promotion.promotion_type === 'items'){
      //   setPromotionName(promotion.promotion_name || '');
      //   setPromotionTypeValue(promotion.promotion_type || '');
      //   setItemsList(promotion.promoted_items || []);
      //   setPromotionPercentage(50);
      //   setSelectedPromotionFeedId('');
      //   setSelectedPromotionFeedName('');
      // }
      // setShowPromotionSection(true);
      // }else{
      //     setPromotionName('');
      //     setSelectedPromotionFeedId('');
      //     setSelectedPromotionFeedName('');
      //     setPromotionPercentage(50);
      //     setItemsList([]);
      //     setShowPromotionSection(false);
      // }
    }
    else {
      setPromotionList([]);
      setItemsList([]);
    }
    if (feed?.config?.filters?.engagement?.hasOwnProperty('min_likes_count') && feed?.config?.filters?.engagement?.min_likes_count >= 0) { 
      setShowEngagementFilters(true);
      setMinLikeCount(feed.config.filters.engagement.min_likes_count);
    }
    else {
      setMinLikeCount('');
    }
    if (feed?.config?.filters?.engagement?.hasOwnProperty('max_likes_count') && feed?.config?.filters?.engagement?.max_likes_count >= 0) { 
      setShowEngagementFilters(true);
      setMaxLikeCount(feed.config.filters.engagement.max_likes_count);
    }
    else {
      setMaxLikeCount('');
    }
    if (feed?.config?.filters?.engagement?.hasOwnProperty('min_comments_count') && feed?.config?.filters?.engagement?.min_comments_count >= 0) { 
      setShowEngagementFilters(true);
      setMinCommentCount(feed.config.filters.engagement.min_comments_count);
    }
    else {
      setMinCommentCount('');
    }
    if (feed?.config?.filters?.engagement?.hasOwnProperty('max_comments_count') && feed?.config?.filters?.engagement?.max_comments_count >= 0) { 
      setShowEngagementFilters(true);
      setMaxCommentCount(feed.config.filters.engagement.max_comments_count);
    }
    else {
      setMaxCommentCount('');
    }
    if (feed?.config?.filters?.engagement?.hasOwnProperty('min_shares_count') && feed?.config?.filters?.engagement?.min_shares_count >= 0) { 
      setShowEngagementFilters(true);
      setMinShareCount(feed.config.filters.engagement.min_shares_count);
    }
    else {
      setMinShareCount('');
    }
    if (feed?.config?.filters?.engagement?.hasOwnProperty('max_shares_count') && feed?.config?.filters?.engagement?.max_shares_count >= 0) { 
      setShowEngagementFilters(true);
      setMaxShareCount(feed.config.filters.engagement.max_shares_count);
    }
    else {
      setMaxShareCount('');
    }
    if (feed?.config?.filters?.app_fids && feed?.config?.filters?.app_fids?.length > 0) { 
      setAppFids(feed.config.filters.app_fids);
      setSelectedIncludeParams((prev) => [...new Set([...prev, 'app_fids'])]);
    }
    else {
      setAppFids([]);
      setSelectedIncludeParams((prev) => prev.filter((param) => param !== 'app_fids'));
    }
    if (feed?.config?.filters?.geo_locations && feed?.config?.filters?.geo_locations?.length > 0) { 
      setSelectedGeoLocations(feed.config.filters.geo_locations.map( savedGeoLocation => getGeloLocationDisplayString(savedGeoLocation)));
      setSelectedIncludeParams((prev) => [...new Set([...prev, 'geo_locations'])]);
    }
    else {
      setSelectedGeoLocations([]);
      setSelectedIncludeParams((prev) => prev.filter((param) => param !== 'geo_locations'));
    }
    if (feed?.config?.filters?.remove_geo_locations && feed?.config?.filters?.remove_geo_locations?.length > 0) { 
      setExcludeGeoLocations(feed.config.filters.remove_geo_locations.map( savedGeoLocation => getGeloLocationDisplayString(savedGeoLocation)));
      setSelectedExcludeParams((prev) => [...new Set([...prev, 'geo_locations'])]);
    }
    else {
      setExcludeGeoLocations([]);
      setSelectedExcludeParams((prev) => prev.filter((param) => param !== 'geo_locations'));
    }

    if (feed?.config?.filters?.channels && feed?.config?.filters?.channels?.length > 0) {
      setIncludeParams((prev) => [...new Set([...prev, 'channels'])]);
      let channelsDetails = await fetchChannelsDetails(feed.config.filters.channels);
      setChannels(channelsDetails.map((ch) => ({ label: ch.name, value: ch.url })));
      setSelectedIncludeParams((prev) => [...new Set([...prev, 'channels'])]);
    }else{
        setChannels([]);
        setIncludeParams((prev) => prev.filter((param) => param !== 'channels')); 
        setSelectedIncludeParams((prev) => prev.filter((param) => param !== 'channels'));
    }

    if (feed?.config?.filters?.remove_channels && feed?.config?.filters?.remove_channels?.length > 0) {
      setExcludeParams((prev) => [...new Set([...prev, 'channels'])]);
      let channelsDetails = await fetchChannelsDetails(feed.config.filters.remove_channels);
      setExcludeChannels(channelsDetails.map((ch) => ({ label: ch.name, value: ch.url })));
      setSelectedExcludeParams((prev) => [...new Set([...prev, 'channels'])]);
    }else{
        setExcludeChannels([]);
        setExcludeParams((prev) => prev.filter((param) => param !== 'channels')); 
        setSelectedExcludeParams((prev) => prev.filter((param) => param !== 'channels'));
    }

    // Set thresholds for include AI labels
    if (feed?.config?.filters?.ai_labels && feed.config.filters.ai_labels.length > 0) {
        const aiLabelsWithThresholds = await Promise.all(feed.config.filters.ai_labels.map(async label => {
          const [labelName, threshold] = label.split(':');
          if (threshold) {
              initialIncludeThresholds[labelName] = parseFloat(threshold); // Store the threshold value
          } 
          const [labelType, userId, labelSet] = labelName.split('/');
          const labelSetDisplay = labelSet;
          if (labelType === 'user' && (labelSet === 'mbd_ai_labels_topics' || labelSet === 'tba_taxonomy_v1')) {
            if (userId === 'user_id') {
              return { label: `current_user:${labelSetDisplay}`, value: labelName };
            }
            else {
              let userDetails = await fetchAuthorDetails([userId]);
              return { label: `${userDetails[0].fname}:${labelSetDisplay}` ,value: labelName };
            }
          }
          return { label: labelName, value: labelName }; // Store as object with label and value
        }));
        setIncludeAILABELS(aiLabelsWithThresholds);
        setSelectedIncludeParams(prev => [...new Set([...prev, 'ai_labels'])]);
    } else {
        setIncludeAILABELS([]);
        setSelectedIncludeParams(prev => prev.filter(param => param !== 'ai_labels'));
    }

    if(feed?.config?.filters?.publication_types && feed?.config?.filters?.publication_types?.length > 0){
        setIncludeParams((prev) => [...new Set([...prev, 'publication_types'])]);
        setIncludePublicationTypes(feed.config.filters.publication_types.filter((type) =>publicationTypesOptions.includes(type)).map((type) => ({ label: type, value: type })));
        setSelectedIncludeParams((prev) => [...new Set([...prev, 'publication_types'])]);
    }else{
        setIncludePublicationTypes([]);
        setIncludeParams((prev) => prev.filter((param) => param !== 'publication_types'));
        setSelectedIncludeParams((prev) => prev.filter((param) => param !== 'publication_types'));
    }
    if(feed?.config?.filters?.languages && feed?.config?.filters?.languages?.length > 0){
      setIncludeParams((prev) => [...new Set([...prev, 'languages'])]);
      setIncludeLanguages(feed.config.filters.languages);
      setSelectedIncludeParams((prev) => [...new Set([...prev, 'languages'])]);
    }else{
        setIncludeLanguages([]);
        setIncludeParams((prev) => prev.filter((param) => param !== 'languages'));
        setSelectedIncludeParams((prev) => prev.filter((param) => param !== 'languages'));
    }

    // Set thresholds for exclude AI labels
    if (feed?.config?.filters?.remove_ai_labels && feed.config.filters.remove_ai_labels.length > 0) {
        const aiLabelsWithThresholds = feed.config.filters.remove_ai_labels.map(label => {
        const [labelName, threshold] = label.split(':');
        if (threshold) {
            initialExcludeThresholds[labelName] = parseFloat(threshold); // Store the threshold value
        }
        return { label: labelName, value: labelName }; // Store as object with label and value
        });
        setExcludeAILABELS(aiLabelsWithThresholds);
        setSelectedExcludeParams(prev => [...new Set([...prev, 'ai_labels'])]);
    } else {
        setExcludeAILABELS([]);
        setSelectedExcludeParams(prev => prev.filter(param => param !== 'ai_labels'));
    }

    // Set the initial AI label thresholds
    setAiLabelThresholds(initialIncludeThresholds);
    setRemoveAILabelThresholds(initialExcludeThresholds);

    if (feed?.config?.filters?.author_ids && feed?.config?.filters?.author_ids?.length > 0) {
      setIncludeParams((prev) => [...new Set([...prev, 'author_ids'])]);
      let authorDetails = await fetchAuthorDetails(feed.config.filters.author_ids);
      setIncludeAuthorIds(authorDetails.map((id) => ({ label: id.fname, value: id.fid })));
      setSelectedIncludeParams((prev) => [...new Set([...prev, 'author_ids'])]);
    }else{
        setIncludeAuthorIds([]);
        setIncludeParams((prev) => prev.filter((param) => param !== 'author_ids')); 
        setSelectedIncludeParams((prev) => prev.filter((param) => param !== 'author_ids'));
    }

    if (feed?.config?.filters?.return_engaged_by_author_ids) {
      setReturnEngagedByAuthorIds(true);
    }

    if (feed?.config?.filters?.remove_author_ids && feed?.config?.filters?.remove_author_ids?.length > 0) {
      setExcludeParams((prev) => [...new Set([...prev, 'author_ids'])]);
      let authorDetails = await fetchAuthorDetails(feed.config.filters.remove_author_ids);
      setExcludeAuthorIds(authorDetails.map((id) => ({ label: id.fname, value: id.fid })));
      setSelectedExcludeParams((prev) => [...new Set([...prev, 'author_ids'])]);
    }else{
        setExcludeAuthorIds([]);
        setExcludeParams((prev) => prev.filter((param) => param !== 'author_ids')); 
        setSelectedExcludeParams((prev) => prev.filter((param) => param !== 'author_ids'));
    }

    if (feed?.config?.filters?.remove_author_ai_labels && feed?.config?.filters?.remove_author_ai_labels?.length > 0){
      setExcludeParams((prev) => [...new Set([...prev, 'author_ai_labels'])]);
      setCustomAiLabelThresholds(feed.config.filters.remove_author_ai_labels);
      setSelectedExcludeParams((prev) => [...new Set([...prev, 'author_ai_labels'])]);
    }
    else {
      setCustomAiLabelThresholds([]);
      setExcludeParams((prev) => prev.filter((param) => param !== 'author_ai_labels'));
      setSelectedExcludeParams((prev) => prev.filter((param) => param !== 'author_ai_labels'));
    }

    if (feed?.config?.filters?.embed_domains && feed?.config?.filters?.embed_domains?.length > 0) {
      setIncludeParams((prev) => [...new Set([...prev, 'embed_domains'])]);
      setEmbedDomains(feed.config.filters.embed_domains);
      setSelectedIncludeParams((prev) => [...new Set([...prev, 'embed_domains'])]);
    }else{
        setEmbedDomains([]);
        setIncludeParams((prev) => prev.filter((param) => param !== 'embed_domains')); 
        setSelectedIncludeParams((prev) => prev.filter((param) => param !== 'embed_domains'));
    }
    if (feed?.config?.context?.app_fid && feed?.config?.context?.app_fid !== '') { 
      setContextualAppFid(feed.config.context.app_fid);
      setSelectedContextualInfoParams((prev) => [...new Set([...prev, 'app_fid'])]);
    }
    else {
      setContextualAppFid('');
      setSelectedContextualInfoParams((prev) => prev.filter((param) => param !== 'app_fid'));
    }
    if (feed?.config?.context?.geo_location && feed?.config?.context?.geo_location !== '') { 
      const geoLocationInfo = geoLocations.find( location => location.location === 'geo:' + feed.config.context.geo_location);
      setSelectedContextualGeoLocation(geoLocationInfo ? feed.config.context.geo_location + ' (' + geoLocationInfo.city || geoLocationInfo.county + ', ' + geoLocationInfo.country + ')' : '');
      setSelectedContextualInfoParams((prev) => [...new Set([...prev, 'geo_location'])]);
    }
    else {
      setSelectedContextualGeoLocation('');
      setSelectedContextualInfoParams((prev) => prev.filter((param) => param !== 'geo_location'));
    }
    setIncludeVideoOrientations([]);
    setMinVideoDuration(0);
    setMaxVideoDuration(0);
    setIncludeVideoLanguages([]);
    setIncludeMiniappCategories([]);
    if (feed?.config?.filters?.publication_types && feed?.config?.filters?.publication_types.includes('video')){
      if (feed?.config?.filters?.video_orientations && feed?.config?.filters?.video_orientations?.length > 0){
        setIncludeVideoOrientations(feed.config.filters.video_orientations.map((orientation) => ({label: orientation, value: orientation})));
      }
      if (feed?.config?.filters?.video_duration_min && feed?.config?.filters?.video_duration_min > 0){
        setMinVideoDuration(feed.config.filters.video_duration_min);
      }
      if (feed?.config?.filters?.video_duration_max && feed?.config?.filters?.video_duration_max > 0){
        setMaxVideoDuration(feed.config.filters.video_duration_max);
      }
      if (feed?.config?.filters?.video_languages && feed?.config?.filters?.video_languages?.length > 0){
        setIncludeVideoLanguages(feed.config.filters.video_languages.map((language) => ({label: language, value: language})));
      }
    }
    if (feed?.config?.filters?.publication_types && feed?.config?.filters?.publication_types.includes('miniapp')){
      if (feed?.config?.filters?.miniapp_categories && feed?.config?.filters?.miniapp_categories?.length > 0){
        setIncludeMiniappCategories(feed.config.filters.miniapp_categories.map((category) => ({label: category, value: category})));
      }
    }else{
      setIncludeMiniappCategories([]);
    }
  };

  const handleScoringChange = (event) => {
    setScoring(event.target.value);
  };

  const handleEndpointChange = (event) => {
    setEndpoint(event.target.value);
    setScoring(scoringOptions[0]);
    if(event.target.value !== 'casts/feed/trending' && event.target.value !== 'casts/feed/popular'){
        setAiLabelThresholds({});
        setRemoveAILabelThresholds({});
    }
  };

  const handleIncludeParamsDialog = () => {
    setOpenIncludeDialog(true);
  };

  const handleExcludeParamsDialog = () => {
    setOpenExcludeDialog(true);
  };

  const handleItemChange = (value, index, field) => {
    const newList = [...itemsList];
    newList[index][field] = value;
    setItemsList(newList);
  };

  const addItem = () => {
    setItemsList([...itemsList, { item_id: '', rank: '' }]);
  };
  
  const removeItem = (index) => {
    const newList = itemsList.filter((_, i) => i !== index);
    setItemsList(newList);
  };
  
  

  const handleIncludeParamsChange = (event) => {
    const { name, checked } = event.target;
    setSelectedIncludeParams((prev) => (checked ? [...prev, name] : prev.filter((item) => item !== name)));
  
    if (!checked) {
      switch (name) {
        case 'app_fids':
          setAppFids([]);
          break;
        case 'geo_locations':
          setSelectedGeoLocations([]);
          break;
        case 'author_ids':
          setIncludeAuthorIds([]);
          break;
        case 'channels':
          setChannels([]);
          break;
        case 'ai_labels':
          setIncludeAILABELS([]);
          setAiLabelThresholds({}); // Reset thresholds when AI labels are unchecked
          break;
        case 'embed_domains':
          setEmbedDomains([]);
          break;
        case 'publication_types':
          setIncludePublicationTypes([]);
          break;
        default:
          break;
      }
    }
  };

  const handleExcludeParamsChange = (event) => {
    const { name, checked } = event.target;
    setSelectedExcludeParams((prev) => (checked ? [...prev, name] : prev.filter((item) => item !== name)));

    if (checked){
      if (name === 'geo_locations' && selectedIncludeParams.includes('geo_locations')){
        setGeoLocationWarningVisible(true);
      }
      else if (name === 'author_ids' && selectedIncludeParams.includes('author_ids')){
        setAuthorIdWarningVisible(true);
      }
    }
    else {
      switch (name) {
        case 'author_ids':
          setExcludeAuthorIds([]);
          setAuthorIdWarningVisible(false);
          break;
        case 'ai_labels':
          setExcludeAILABELS([]);
          setRemoveAILabelThresholds({}); // Reset thresholds when AI labels are unchecked
          break;
        case 'geo_locations':
          setExcludeGeoLocations([]);
          setGeoLocationWarningVisible(false);
          break;
        case 'channels':
          setExcludeChannels([]);
          break;
        default:
          break;
      }
    }
  };  

  const handleContextualInfoParamsChange = (event) => {
    const { name, checked } = event.target;
    setSelectedContextualInfoParams((prev) => (checked ? [...prev, name] : prev.filter((item) => item !== name)));
  
    if (!checked) {
      switch (name) {
        case 'app_fids':
          setContextualAppFid('');
          break;
        case 'geo_locations':
          setSelectedContextualGeoLocation('');
          break;
        default:
          break;
      }
    }
  };  

  const handleSave = async () => {
    const config = {
      name: feedName,
      api_key: 'all',
      description,
      endpoint,
      status: 'active',
      visibility: 'private',
      config: {
        // context: {
        //   app_fid: contextualAppFid || '',
        //   geo_location: selectedContextualGeoLocation && selectedContextualGeoLocation !== '' && selectedContextualGeoLocation.includes(' ') ? selectedContextualGeoLocation.split(' ')[0] : '',
        // },
        name: feedName,
        filters: {
          channels: channels.length > 0 ? channels.map((ch) => ch.value) : [],
          remove_channels: excludeChannels.length > 0 ? excludeChannels.map((ch) => ch.value) : [],
          app_fids: appFids,
          geo_locations: selectedGeoLocations.map(location => location.split(' ')[0]),
          remove_geo_locations: excludeGeoLocations.map(location => location.split(' ')[0]),
          ai_labels: includesAILABELS.map(label => aiLabelThresholds[label.value] ? `${label.value}:${aiLabelThresholds[label.value]}` : label.value),
          publication_types: includePublicationTypes.length > 0 ? includePublicationTypes.map((type) => type.value): [],
          languages: includeLanguages.length > 0 ? includeLanguages : [],
          remove_ai_labels: excludeAILABELS.map(label => removeAILabelThresholds[label.value] ? `${label.value}:${removeAILabelThresholds[label.value]}` : label.value),
          author_ids: includeAuthorIds.length > 0 ? includeAuthorIds.map((id) => id.value) : [],
          remove_author_ids: excludeAuthorIds.length > 0 ? excludeAuthorIds.map((id) => id.value) : [],
          embed_domains: embedDomains.length > 0 ? embedDomains : [],
        },
        return_global_fallback: !dontReturnGlobalFallback,
      },
    };
    if (endpoint === 'casts/feed/for-you'){
      config.config.scoring = scoring;
    }
    if (includeAuthorIds.length > 0) {
      config.config.filters.return_engaged_by_author_ids = returnEngagedByAuthorIds;
    }

    if (showFeedDiversitySection && (maxPostsPerAuthor > 0 || minDistanceBetweenPostsFromSameAuthor > 0)) {
      let feedDiversityConfig = {}
      if (maxPostsPerAuthor > 0){
        feedDiversityConfig.max_posts_per_author = Number(maxPostsPerAuthor);
      }
      if (minDistanceBetweenPostsFromSameAuthor > 0){
        feedDiversityConfig.min_distance_between_posts_from_same_author = Number(minDistanceBetweenPostsFromSameAuthor);
      }
      config.config.feed_diversity_config = feedDiversityConfig;
    }

    if (customAiLabelThresholds.length > 0){
      config.config.filters.remove_author_ai_labels = customAiLabelThresholds;
    }
  
    if (showPromotionSection && promotionTypeValue && promotionTypeValue === 'feed' && promotionName && promotionName !== '' && selectedPromotionFeedId && selectedPromotionFeedId !== '') {
      config.config.promotion_filters = [{
        promotion_type: promotionTypeValue,
        promotion_name: promotionName,
        feed_id: selectedPromotionFeedId,
        percent: promotionPercentage
      }];
    }

    if (promotionList.length > 0 || itemsList.length > 0){
      config.config.promotion_filters = [];
      config.config.promotion_filters.push(...promotionList);
      config.config.promotion_filters.push({
        promotion_type: 'items',
        promotion_name: 'promoted_items',
        promoted_items: itemsList
      });
    }

    if (showPromotionSection && promotionTypeValue && promotionTypeValue === 'items' &&  promotionName && promotionName !== '' && itemsList.length > 0) {
      config.config.promotion_filters = [{
        promotion_name: promotionName,
        promotion_type: promotionTypeValue,
        promoted_items: itemsList
      }];
    }

    if(showInteracted){
      config.config.show_interacted_items = true;
    }

    if(removePostsFromCurrentUser){
      config.config.remove_user_posts_from_feed = true;
    }

    if(withSocialProofOnly){
      config.config.return_only_posts_with_social_proof = true;
    }

    if(useTimeDecay){
      config.config.time_decay = timeDecayValue;
    }

    // if(showFallbackSection && fallbackFeedId && fallbackFeedId !== '') {
    //   config.config.global_fallback = {
    //     global_fallback_feed: {
    //       feed_id: fallbackFeedId
    //     }
    //   };
    // } else {
    //   delete config.config.global_fallback;
    // }
    if(showFallbackSection && fallbackFeedList.length > 0){
      config.config.fallback_feeds = fallbackFeedList.map((feed) => ({feed_id: feed}));
    }

    if (showColdStartSection && coldStartFeedId && coldStartFeedId !== '') {
      config.config.cold_start = {
        cold_start_feed: {
          feed_id: coldStartFeedId
        }
      };
      if (coldStartThreshold !== '' && Number(coldStartThreshold) > 0){
        config.config.cold_start.cold_start_threshold_days = Number(coldStartThreshold);
      }
      if (coldStartMinimumInteractionCount !== '' && Number(coldStartMinimumInteractionCount) > 0){
        config.config.cold_start.min_interactions_count = Number(coldStartMinimumInteractionCount);
      }
    } else {
      delete config.config.cold_start;
    }


    if(timePeriod && timePeriod > 0){
      config.config.filters.start_timestamp = timePeriodUnit === 'days' ? `days_ago:${timePeriod}` : `hours_ago:${timePeriod}`;
    }

    if (showEngagementFilters){
      let engagementFilters = {}
      if(minLikeCount !== '' && minLikeCount >= 0){
        engagementFilters.min_likes_count = Number(minLikeCount);
      }
      if(maxLikeCount !== '' && maxLikeCount >= 0){
        engagementFilters.max_likes_count = Number(maxLikeCount);
      }
      if(minCommentCount !== '' && minCommentCount >= 0){
        engagementFilters.min_comments_count = Number(minCommentCount);
      }
      if(maxCommentCount !== '' && maxCommentCount >= 0){
        engagementFilters.max_comments_count = Number(maxCommentCount);
      }
      if(minShareCount !== '' && minShareCount >= 0){
        engagementFilters.min_shares_count = Number(minShareCount);
      }
      if(maxShareCount !== '' && maxShareCount >= 0){
        engagementFilters.max_shares_count = Number(maxShareCount);
      }
      if(Object.keys(engagementFilters).length > 0){
        config.config.filters.engagement = engagementFilters;
      }
    }
    if (selectedIncludeParams.includes('publication_types') && includePublicationTypes.map((type) => type.value).includes('video')){
      if (includeVideoOrientations.length > 0){
        config.config.filters.video_orientations = includeVideoOrientations.map((orientation) => orientation.value);
      }
      if (minVideoDuration !== '' && minVideoDuration > 0){
        config.config.filters.video_duration_min = Number(minVideoDuration);
      }
      if (maxVideoDuration !== '' && maxVideoDuration > 0){
        config.config.filters.video_duration_max = Number(maxVideoDuration);
      }
      if (includeVideoLanguages.length > 0){
        config.config.filters.video_languages = includeVideoLanguages.map((language) => language.value);
      }
    }
    if (selectedIncludeParams.includes('publication_types') && includePublicationTypes.map((type) => type.value).includes('miniapp')){
      if (includeMiniappCategories.length > 0){
        config.config.filters.miniapp_categories = includeMiniappCategories.map((category) => category.value);
      }
    }else{
      setIncludeMiniappCategories([]);
    }
    try {
      if (isNewFeed) {
        // if(impressionCount && impressionCount >= 0){
        //   config.config.impression_count = impressionCount;
        // }
        const response = await Axios.post('/api/feed/config', config);
        setSavedConfigId(response?.data?.data?.config_id);
        setFeedId(response?.data?.data?.config_id);
        setSnackbarMessage(`Feed created successfully! Your feed ID is ${response?.data?.data?.config_id}. Please use this feed ID in the API.`);
        setShowSnackbar(true);
        setShowSaveAs(true);
        setIsNewFeed(false);
        // setIsPreviewOpen(true);
        setIsEditMode(false);
      } else {
        // if(impressionCount && impressionCount > 0){
        //   config.config.impression_count = Number(impressionCount);
        // }
        config.config_id = feedId;
        await Axios.patch('/api/feed/config', config);
        setSnackbarMessage('Feed updated successfully');
        setShowSnackbar(true);
        setShowSaveAs(true);
        // setIsPreviewOpen(true);
        setIsEditMode(false);
      }
      setIframeKey(Date.now());
    } catch (error) {
      console.error('Error saving feed configuration:', error);
      setSnackbarMessage(`Failed to save configuration ${error}`);
      setShowSnackbar(true);
    } finally {
      // Re-fetch both public and private feeds after saving or updating
      fetchAllFeeds();
    }
  }; 

  const handleSaveAs = () => {
    setIsNewFeed(true);
    setFeedId('');
    setVisibility('private');
    setFeedName(`${feedName}-copy`);
    setSnackbarMessage('Prepare to save as a new feed');
    setShowSnackbar(true);
    setShowSaveAs(false);
    setIsEditMode(true); 
    // setIsPreviewOpen(true);
  };

  const handleDelete = async () => {
    viewContext.modal.show({

      title: 'Delete Feed',
      form: {
        config_id: {
          type: 'hidden',
          value: feedId,
        }
      },
      buttonText: 'Delete Feed',
      url: '/api/feed/config',
      method: 'DELETE',
      destructive: true,
      text: 'Are you sure you want to delete this feed? ' +
      'If you are using this feed_id in your application, it will stop working.',

    }, () => {

      Event('feed_builder_delete_feed');
      handlePostDelete();

    });
  }

  const handlePostDelete = async () => {
    try {
  
      // Show success message in snackbar
      setSnackbarMessage('Feed Config deleted successfully');
      setShowSnackbar(true);
  
    const [publicFeedsData, privateFeedsData] = await Promise.all([
        fetchFeeds('template'),
        fetchFeeds('private'),
    ]);

    // Set the state for public and private feeds
    setPublicFeeds(publicFeedsData);
    setPrivateFeeds(privateFeedsData);
      
      // Reset the form fields and select the first feed from the updated list
      const updatedFeeds = visibility === 'public' ? publicFeedsData : privateFeedsData;
      if (updatedFeeds.length > 0) {
        const firstFeed = updatedFeeds[0];
        resetFormFields(); // Clear all form fields
        setFeedId(firstFeed.config_id);
        populateFeedDetails(firstFeed);
        // setIsPreviewOpen(true);
      } else {
        // If no feeds are left, reset the fields
        resetFormFields();
      }
  
    } catch (error) {
      console.error('Error deleting feed configuration:', error);
      setSnackbarMessage('Failed to delete the feed configuration');
      setShowSnackbar(true);
    }
  };

  const handleCancelEdit = async () => {
    // viewContext.modal.show({

    //   title: 'Cancel Edit',
    //   buttonText: 'OK',
    //   destructive: true,
    //   text: 'Cancel editing this feed? Unsaved changes will be lost.'

    // }, async () => {

      // TODO: show a confirmation dialog before cancelling edit

      setIsEditMode(false);
      const myFeeds = await fetchFeeds('private');
      const currentFeed = myFeeds.find((feed) => feed.config_id === feedId);
      populateFeedDetails(currentFeed);

    // });
  }

  const handleDeleteIncludeAILabel = (event, labelToDelete) => {
    event.stopPropagation();
    const newIncludesAILabels = includesAILABELS.filter((label) => label.label !== labelToDelete);
    setIncludeAILABELS(newIncludesAILabels);
  }

  const handleDeleteExcludeAILabel = (event, labelToDelete) => {
    event.stopPropagation();
    const newExcludeAILabels = excludeAILABELS.filter((label) => label.label !== labelToDelete);
    setExcludeAILABELS(newExcludeAILabels);
  }
  
  useEffect(() => {
    const fetchInitialFeeds = async () => {
      // Fetch feeds data directly
      const [publicFeedsData, privateFeedsData] = await Promise.all([
        fetchFeeds('template'),
        fetchFeeds('private'),
      ]);
  
      // Set the state for public and private feeds
      setPublicFeeds(publicFeedsData);
      setPrivateFeeds(privateFeedsData);
  
      // Determine which feeds to use based on visibility and `feedId`
      const initialFeeds = visibility === 'public' ? publicFeedsData : privateFeedsData;


      
      // If `feedId` exists in query, find and select it
      if ( mode !== 'template') {
        if (initialFeedId) {
          const selectedFeed = [...publicFeedsData, ...privateFeedsData].find(feed => feed.config_id === initialFeedId);
          if (selectedFeed) {
            setIsShowcaseMode(false);
            setVisibility(mode === 'public' ? 'public' : 'private');
            setFeedId(selectedFeed.config_id);
            populateFeedDetails(selectedFeed);

            return;
          }
        }
    
        // Default to the first available feed if no `feedId` is provided
        if (initialFeeds.length > 0) {
          const firstFeed = initialFeeds[0];
          resetFormFields(); // Clear all form fields
          setFeedId(firstFeed.config_id); // Set the Feed ID
          populateFeedDetails(firstFeed); // Populate the form fields with feed details
        }
      }
    };

    fetchInitialFeeds();

    if(location.state?.initialNewFeed){
      populateFeedDetails(location.state.initialNewFeed);
      setIsNewFeed(true);
      setIsShowcaseMode(false);
      setVisibility('private');
      setIsEditMode(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  
  }, []);  

  useEffect(() => {
    if (selectedIncludeParams.includes('geo_locations') && selectedExcludeParams.includes('geo_locations')){
      setGeoLocationWarningVisible(true);
    }
    else {
      setGeoLocationWarningVisible(false);
    }
    if (selectedIncludeParams.includes('author_ids') && selectedExcludeParams.includes('author_ids')){
      setAuthorIdWarningVisible(true);
    }
    else {
      setAuthorIdWarningVisible(false);
    }
  }, [selectedIncludeParams, selectedExcludeParams]);

  const findNewItem = (oldList, newList) => {
    return newList.find((item) => !oldList.some((oldItem) => 
      (typeof oldItem === 'string' ? oldItem : oldItem.value) === (typeof item === 'string' ? item : item.value)
    ));
  }

  const selectTemplate = (feed) => {  
    Event('feed_builder_select_template');
    setFeedId(feed.config_id);
    populateFeedDetails(feed);
    setIsShowcaseMode(false);
  }

  const createNewFeed = () => {
    resetFormFields();
    setIsNewFeed(true);
    setFeedName('New Feed');
    setDescription('New Feed');
    setEndpoint('casts/feed/trending');
    setStatus('active');
    setIsShowcaseMode(false);
    setIsEditMode(true);
    setVisibility('private');
  }

  const editFeed = (feed) => {
    Event('feed_builder_select_my_feed');
    setFeedId(feed.config_id);
    populateFeedDetails(feed);
    setIsEditMode(true);
    setIsShowcaseMode(false);
    setVisibility('private');
  }

  const copyCode = () => {
    Event('feed_builder_copy_code');
    const code = getCodeString(authContext.user?.farcaster_profile?.fid, feedId, endpoint, authContext.user?.api_key, true );
    navigator.clipboard.writeText(code);
  }

  const handleSavePromotion = () => {

    setShowAddPromotionSection(false);
    const promotion = {
      promotion_type: promotionTypeValue,
      feed_id: selectedPromotionFeedId,
      percent: promotionPercentage,
      skew: skew,
      promotion_name: promotionName,
    }
    if(promotionEditIndex === -1){
      setPromotionList([...promotionList, promotion]);
    }
    else{
      setPromotionList(promotionList.map((p, index) => index === promotionEditIndex ? promotion : p));
    }
    setPromotionEditIndex(-1);
  }
  
  const NonEditableTextField = (props) => 
    <Box  mr={4} mt={4} sx={{display:'flex', flexDirection: props.horizontal ? 'row' : 'column', alignItems: props.horizontal ? 'center' : 'flex-start'}} >
      <Text variant={props.headingVariant || 'fieldHeading'} color={props.headingColor || 'brand-light'} sx={props.sx}>
        {props.heading}
        {
            props.showExplanation &&
            <IconButton
              ml={1}
              onClick={() => {setOpenInfoDialog(true); 
                setInfoDialogTitle(props.explanationTitle); 
                setInfoDialogText(props.explanationDescription)
              }}
            >
              <Tooltip title={`What is ${props.heading.toLowerCase()}?`}>
                <QuestionIcon sx={{color:colors['gray-lighter']}}/>
              </Tooltip>
            </IconButton>
          }
      </Text>
      <Text variant='fieldValue' color={props.value === 'None' || props.valueNotSet ? 'gray-light' : 'gray'} sx={{marginLeft: props.horizontal ? 2 : 0, marginTop: props.horizontal ? 0 : 2}}>
        {props.value}
      </Text>
    </Box>

  const SelectField = (props) =>
  {
    return (
      <Box sx={{display:'flex', flexDirection: props.horizontal ? 'row' : 'column', alignItems: props.horizontal ? 'center' : 'flex-start'}} fullWidth >
        <Text variant='fieldHeading' color={props.headingColor || 'brand-light'}>
          {props.heading}
        </Text>
        <FormControl variant="outlined" sx={{width: props.width ? props.width : '100%'}}>
          <Select 
            value={props.value} 
            onChange={props.onChange} 
            width='600px'
            sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: primaryColor }, marginLeft: props.horizontal ? 2 : 0, marginTop: props.horizontal ? 0 : 2}}
          >
            {props.options.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    )
  }
  

  const AutoCompleteField = (props) =>
    <Box mt={4}>
      <Text variant='fieldHeading' color='brand-light'>
        {props.heading}
      </Text>
      <Autocomplete
        multiple
        options={props.options}
        value={props.value}
        onInputChange={props.onInputChange}
        onChange={props.onChange}
        renderInput={props.renderInput}
        sx={{ my: 2 }}
      />
    </Box>
  const NonEditableSelections = (props) => {
    return ( props.hideIfEmpty && props.selections.length === 0 ) ? <></> : (
      <Box>
        <Box my={2}>
          <Text variant='fieldHeading' color='brand-light'>{props.heading}</Text>
        </Box>
        {
          props.selections.length > 0 ? 
            <Stack direction='row' spacing={1} sx={{flexWrap: 'wrap', gap: '16px 8px'}}>
              {props.selections.map((selection) => <Chip margin={2} label={selection} variant='outlined' key={selection} sx={{color:'#97979A'}}/>)}
            </Stack>
          : 
            <Text variant='fieldValue' color='gray-light'>None</Text>
        }
      </Box>
    )
  }

  const NonEditableCheckboxField = (props) =>
    <Stack direction='row' spacing={1} alignItems='center' sx={{ mt:4 }}>
      <Text variant='fieldHeading' color={props.headingColor || 'brand-light' } >
        {props.text}
      </Text>
      <Text variant='fieldValue' >
        {props.checked ? 'Yes' : 'No'}
      </Text>
    </Stack>

  const Promotion = () =>

      <Box>
        {
          promotionList.length > 0 &&
          <Box>
            <Box mt={2}>
              <Text variant='h3'>Feeds</Text>
            </Box>
            {promotionList.map((promotion, index) => {
                return (
                  <Stack direction='row' spacing={2} alignItems={'center'} my={1}>
                    <Stack direction='row' spacing={1} flex={1}>
                      <Text>{`${index + 1}.`}</Text>
                      <Text variant='small' sx={{backgroundColor: colors['gray-lightest'], padding: '2px 4px', borderRadius: 1}}>{`${promotion.percent}%`}</Text>  
                      <Text>{`${promotion.promotion_name} (${promotion.feed_id})`}</Text>
                      <Stack direction='row' alignItems={'center'}>
                        <IconButton sx={{width: 14, height: 14}}>
                          <SkewIcon sx={{fontSize: 12, color: indigo[300]}} />
                        </IconButton>
                        <Text color='indigo'>{`${promotion.skew}`}</Text>
                      </Stack>
                    </Stack>
                  </Stack>
                )
              })
            }
          </Box>
        }
        {
          itemsList.length > 0 &&
          <Box>
            <Box mt={2}>
              <Text variant='h3'>Items</Text>
            </Box>
            <Box mt={2}>
              <Stack direction='row' spacing={4}>
                <Box>
                  <Text variant='fieldHeading' color='brand-light'> Item ID </Text>
                  {
                    itemsList.map((item) => <Box mt={1}><Text variant='fieldValue' color='gray-light'> {item.item_id} </Text></Box>)
                  }
                </Box>
                <Box ml={4}>
                  <Text variant='fieldHeading' color='brand-light'> Rank </Text>
                  {
                    itemsList.map((item) => <Box mt={1}><Text variant='fieldValue' color='gray-light'> {item.rank} </Text></Box>)
                  }
                </Box>
              </Stack>
            </Box>          
          </Box>
          
        }

        {/* <NonEditableTextField horizontal={true} heading="Promotion Type" value={promotionTypeValue}/> 
        {
          promotionTypeValue === 'feed' ?
          <Box>
            <Box display="flex" flexDirection={'row'}>
              <NonEditableTextField horizontal={true} heading="Feed ID" value={selectedPromotionFeedId}/>
              <NonEditableTextField horizontal={true} heading="Feed Name" value={selectedPromotionFeedName}/>
            </Box>
            <Box display="flex" flexDirection={'row'}>
              <NonEditableTextField horizontal={true} heading="Promotion Name" value={promotionName}/>
              <NonEditableTextField horizontal={true} heading="Percentage" value={promotionPercentage}/>
            </Box>
          </Box>
        :
          <Box mt={2}>

            <Stack direction='row' spacing={4}>
              <Box>
                <Text variant='fieldHeading' color='brand-light'> Item ID </Text>
                {
                  itemsList.map((item) => <Box mt={1}><Text variant='fieldValue' color='gray-light'> {item.item_id} </Text></Box>)
                }
              </Box>
              <Box ml={4}>
                <Text variant='fieldHeading' color='brand-light'> Rank </Text>
                {
                  itemsList.map((item) => <Box mt={1}><Text variant='fieldValue' color='gray-light'> {item.rank} </Text></Box>)
                }
              </Box>
            </Stack>
          </Box>
        } */}
      </Box>

    const FallbackConfigurations = () =>
        
      <Box my={4}>
        {
          fallbackFeedList.map((feedId, index) => (
            <Stack mt={1} direction='row' spacing={2} alignItems={'center'} key={'fallback-'+index}>
              <Stack flexGrow={1} direction='row' spacing={2} alignItems={'center'}>
                <Box mr={6}>
                  <Text variant='body'>{index+1}.</Text>
                </Box>
                <NonEditableTextField horizontal={true} heading="Feed ID" value={feedId}/>
                <NonEditableTextField horizontal={true} heading="Feed Name" value={[...publicFeeds, ...privateFeeds].find((f) => f.config_id === feedId).name}/>
              </Stack>
            </Stack>
          ))                                        
        }
      </Box>

    const ColdStartUserConfigurations = () =>
        
        <Box mt={4}>
         <Text variant='h2'>Cold Users Feed</Text>
          <Box display="flex" flexDirection={'row'}>
            <NonEditableTextField horizontal={true} heading="Feed Id" value={coldStartFeedId}/>
          </Box>
          {
            coldStartThreshold !== '' &&
            <NonEditableTextField horizontal={true} heading="Cold Start Threshold" value={coldStartThreshold}/>
          }
          {
            coldStartMinimumInteractionCount !== '' &&
            <NonEditableTextField horizontal={true} heading="Minimum Interaction Count" value={coldStartMinimumInteractionCount}/>
          }
        </Box>
    


  
  const NonEditableFeedDetails = () =>
    <Box my={1}>

      <Box display="flex" flexDirection={'row'} alignItems={'center'}>
        <Box sx={{flexGrow: 1}}>
        </Box>

      </Box>
      <Card title='1. Candidate Generation'>
        <NonEditableTextField horizontal={true} heading="Time Period" value={`${timePeriod} ${timePeriodUnit}`}/>
        <Box mt={4}>
        <Text variant='h2'>Include Params</Text>
          {
            appFids.length == 0 && selectedGeoLocations.length == 0 && includeAuthorIds.length == 0 && 
            channels.length == 0 && includesAILABELS.length == 0 && embedDomains.length == 0 && 
            includePublicationTypes.length == 0 && includeLanguages.length == 0 ?
            <Box mt={2}>
              <Text variant='fieldValue' color='gray-light'>None</Text>
            </Box>
            :
            <Box>
              <NonEditableSelections hideIfEmpty heading="Source App FIDs" selections={ appFids }/>
              <NonEditableSelections hideIfEmpty heading="Geo Locations" selections={ selectedGeoLocations }/>
              <NonEditableSelections hideIfEmpty heading="Author IDs" selections={ includeAuthorIds.map((author) => author.label)}/>
              {
                includeAuthorIds.length > 0 &&  
                <NonEditableCheckboxField text="Return posts that the users listed above have interacted with?" headingColor='gray-lighter' checked={returnEngagedByAuthorIds}/>
              }
              <NonEditableSelections hideIfEmpty heading="Channels" selections={channels.map((channel) => channel.label)}/>
              <NonEditableSelections hideIfEmpty heading="AI Labels" selections={includesAILABELS.map(label => label.label)}/>
              <NonEditableSelections hideIfEmpty heading="Embed Domains" selections={embedDomains}/>
              <NonEditableSelections hideIfEmpty heading="Publication Types" selections={includePublicationTypes.map(type => type.label)}/>
              {
                includePublicationTypes.map((type) => type.value).includes('video') && (
                  <>
                    {includeVideoOrientations.length > 0 &&
                        <NonEditableSelections hideIfEmpty heading="Video Orientations" selections={includeVideoOrientations.map(orientation => orientation.label)}/>
                    }
                    {(minVideoDuration > 0 || maxVideoDuration > 0) &&
                        <Stack direction='row' spacing={4} sx={{ mt:2 }}>
                          <Text variant='fieldHeading' color='brand-light' sx={{minWidth: 150}}>
                            Video Duration
                          </Text>
                          <NonEditableTextField horizontal={true} heading="min" headingVariant='small' headingColor='gray-lighter' value={minVideoDuration > 0 ? minVideoDuration : 'None'} sx={{ fontSize: '10px'}}/> 
                          <NonEditableTextField horizontal={true} heading="max" headingVariant='small' headingColor='gray-lighter' value={maxVideoDuration > 0 ? maxVideoDuration : 'None'} sx={{ fontSize: '10px'}}/>
                        </Stack>
                    }
                    {includeVideoLanguages.length > 0 &&
                      <NonEditableSelections hideIfEmpty heading="Video Languages" selections={includeVideoLanguages.map(language => language.label)}/>
                    }
                  </>
                )
              }
              {
                includePublicationTypes.map((type) => type.value).includes('miniapp') && 
                  <NonEditableSelections hideIfEmpty heading="Miniapp Categories" selections={includeMiniappCategories.map(category => category.label)}/>
              }
              <NonEditableSelections hideIfEmpty heading="Languages" selections={ includeLanguages}/>              
            </Box>
          }

        </Box>
        <Box mt={4}>
          <Text variant='h2'>Exclude Params</Text>
          {
            excludeAuthorIds.length === 0 && excludeAILABELS.length === 0 && excludeGeoLocations.length === 0 && excludeChannels.length === 0 && customAiLabelThresholds.length === 0 ?
              <Box mt={2}>
                <Text variant='fieldValue' color='gray-light'>None</Text>
              </Box>
            :
              <Box mt={2}>
                <NonEditableSelections hideIfEmpty heading="Author IDs" selections={excludeAuthorIds.map((author) => author.label)}/>
                <NonEditableSelections hideIfEmpty heading="AI Labels" selections={excludeAILABELS.map(label => label.label)}/>
                <NonEditableSelections hideIfEmpty heading="Geo Locations" selections={excludeGeoLocations}/>        
                <NonEditableSelections hideIfEmpty heading="Channels" selections={excludeChannels.map((channel) => channel.label)}/>
                {
                  customAiLabelThresholds.length > 0 && 
                  
                  <Box mt={2}>
                    <Text variant='fieldHeading' color='brand-light'>
                      Author AI Labels
                    </Text>
                    {customAiLabelThresholds.map((threshold, index) => {
                      const labelName = Object.keys(threshold)[0];
                      const thresholdValue = threshold[labelName].threshold;
                      const condition = threshold[labelName].condition;
                      const labelThresholdString = `${labelName}: ${thresholdValue} `;
                      const conditionString = condition.feature ? `(${condition.feature} ${condition.operator} ${condition.value})` : '';
                      return (
                        <Stack direction='row' spacing={1} mt={1} alignItems={'center'} minHeight={40}> 
                          <Text>{`${index + 1}. `}</Text>
                          <Text sx={{backgroundColor: colors['gray-lightest'], padding: '2px 4px', borderRadius: 1}}>{labelThresholdString}</Text>
                          <Text sx={{flexGrow: 1}}>{conditionString}</Text>
                        </Stack>
                      )
                    })}
                  </Box>
                }
              </Box>
          }
        </Box>
        <Box mt={4}>
          <Text variant='h2'>Engagement</Text>
        </Box>
        <Box my={2}>
        {
          (minLikeCount !== '' || maxLikeCount !== '') &&
          <Stack direction='row' spacing={4} sx={{ mt:2 }}>
            <Text variant='fieldHeading' color='brand-light' sx={{minWidth: 150}}>
              Likes
            </Text>
            <NonEditableTextField horizontal={true} heading="min" headingVariant='small' headingColor='gray-lighter' value={minLikeCount !== '' ? minLikeCount : 'None'} sx={{ fontSize: '10px'}}/> 
            <NonEditableTextField horizontal={true} heading="max" headingVariant='small' headingColor='gray-lighter' value={maxLikeCount !== '' ? maxLikeCount : 'None'} sx={{ fontSize: '10px'}}/>
          </Stack>
        }
        {
          (minCommentCount !== '' || maxCommentCount !== '') &&
          <Stack direction='row' spacing={4} sx={{ mt:2 }}>
            <Text variant='fieldHeading' color='brand-light' sx={{minWidth: 150}}>
              Comments
            </Text>
            <NonEditableTextField horizontal={true} heading="min" headingVariant='small' headingColor='gray-lighter' value={minCommentCount !== '' ? minCommentCount : 'None'}/> 
            <NonEditableTextField horizontal={true} heading="max" headingVariant='small' headingColor='gray-lighter' value={maxCommentCount !== '' ? maxCommentCount : 'None'}/>
          </Stack>
        }
        {
          (minShareCount !== '' || maxShareCount !== '') &&
          <Stack direction='row' spacing={4} sx={{ mt:2 }}>
            <Text variant='fieldHeading' color='brand-light' sx={{minWidth: 150}}>
              Shares
            </Text>
            <NonEditableTextField horizontal={true} heading="min" headingVariant='small' headingColor='gray-lighter' value={minShareCount !== '' ? minShareCount : 'None'}/> 
            <NonEditableTextField horizontal={true} heading="max" headingVariant='small' headingColor='gray-lighter' value={maxShareCount !== '' ? maxShareCount : 'None'}/>
          </Stack>
        }
        {
          minLikeCount === '' && maxLikeCount === '' &&
          minCommentCount === '' && maxCommentCount === '' &&
          minShareCount === '' && maxShareCount === '' &&
          <Text variant='fieldValue' color='gray-light'>None</Text>
        }
        </Box>
      </Card>

      <Card title='2. Ranking'>
        <Box mt={2}>
          {
            endpoint === 'casts/feed/for-you' &&
            <NonEditableTextField horizontal={true} heading="Scoring" value={scoring}/>
          }
          <NonEditableTextField horizontal={true} heading="Time Decay" value={ useTimeDecay ? timeDecayValue : 'None'}/>
        </Box>
      </Card>

      <Card title='3. Visibility Filters'>
        <Box display="flex" flexDirection={'row'}>
        </Box>

        {/* <NonEditableTextField horizontal={true} heading="Impression Count" value={impressionCount}/> */}
        <NonEditableCheckboxField text="Show items user has already interacted with?" checked={showInteracted}/>
        <NonEditableCheckboxField text="Remove posts created by the current user?" checked={removePostsFromCurrentUser}/>
        <NonEditableCheckboxField text="Return only posts with social proof?" checked={withSocialProofOnly}/>

        <Box mt={4}>
          <Text variant='h2'>Feed Diversity</Text>
        </Box>
        <Box my={2}>
          {
            showFeedDiversitySection ?
              <>
                <NonEditableTextField horizontal={true} heading="Maximum Posts from Same Author" value={maxPostsPerAuthor > 0 ? maxPostsPerAuthor : 'None'}/>
                <NonEditableTextField horizontal={true} heading="Minimum Distance Between Posts from Same Author" value={minDistanceBetweenPostsFromSameAuthor > 0 ? minDistanceBetweenPostsFromSameAuthor : 'None'}/>
              </>
            :
            <Text variant='fieldValue' color='gray-light'>None</Text>
          }
        </Box>
      </Card>

      <Card title='4. Feed Construction'>
        <Box mt={4}>
          <Text variant='h2' sx={{mb: 2}} > Fallback Configurations</Text>
          {
            showFallbackSection ?
              <FallbackConfigurations />
            : 
              <Text variant='fieldValue' color='gray-light'>None</Text>
          }
        </Box>


        <Box mt={4}>
          {
            showColdStartSection ?
              <ColdStartUserConfigurations />
            : 
              <Box>
                <Text variant='h2' sx={{mb: 2}} > Cold Start User Configurations</Text>
                <Text variant='fieldValue' color='gray-light'>None</Text>
              </Box>
          }
        </Box>
        <Box mt={4}>
          <Text variant='h2' sx={{mb: 2}}>Promotions</Text>
          {
            promotionList.length > 0 || itemsList.length > 0 ?
              <Promotion /> 
            : 
              <Text variant='fieldValue' color='gray-light'>None</Text>
          }
        </Box>
      </Card>  
      {/* <Box mt={4}>
        <Text variant='h1' gap={2} > Contextual Information</Text>

        <NonEditableTextField horizontal={false} heading="App FID" value={contextualAppFid !== '' ? contextualAppFid : 'None'}/>
        <NonEditableTextField horizontal={false} heading="Geo Location" value={selectedContextualGeoLocation !== '' ? selectedContextualGeoLocation : 'None'}/>

      </Box> */}
    </Box>

  const FeedEditControls = () =>
  {
    return (
      <>
      {
        isEditMode ?
        <Stack direction={'row'} fullWidth>
          { !isNewFeed &&
            <IconButton onClick={handleCancelEdit} sx={{width: 40, padding: 1, fontSize: 'small'}}>
              <Tooltip title="Cancel editing feed">
                <UndoIcon />
              </Tooltip>
            </IconButton> 
          }
          { !isNewFeed &&
            <IconButton onClick={() => {Event("feed_builder_clone_feed"); handleSaveAs()}} sx={{width: 40, padding: 1, fontSize: 'small'}}>
              <Tooltip title="Clone this feed as a new feed">
                <CloneIcon />
              </Tooltip>
            </IconButton> 
          }
          { !isNewFeed &&
            <IconButton onClick={handleDelete} sx={{width: 40, marginRight: 1}}>
              <Tooltip title="Delete Feed">
                <DeleteIcon />
              </Tooltip>
            </IconButton>
          }
          <PrimaryButton onClick={() => {Event("feed_builder_save_feed"); handleSave()}}> Save Feed </PrimaryButton>   
        </Stack>
      :
        <Stack direction={'row'} fullWidth>
          <IconButton onClick={ 
            () => {
              Event('feed_builder_clone_feed');
              handleSaveAs();
            }} 
            sx={{width: 40, padding: 1, 
            fontSize: 'small'}}>
              <Tooltip title="Clone this feed as a new feed">
                <CloneIcon />
              </Tooltip>
          </IconButton> 
          <IconButton onClick={handleDelete} sx={{width: 40, marginRight: 1}}>
            <Tooltip title="Delete Feed">
              <DeleteIcon />
            </Tooltip>
          </IconButton>
          <PrimaryButton onClick={() => { Event('feed_builder_edit_feed'); setIsEditMode(true)}}> Edit Feed </PrimaryButton>  
        </Stack>
      }
      </>
    )

  }
  

  const MetricsArea = () =>          {

    if (isMetricsLoading) {
      return  <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
                <Loader />
              </Box>
    }
    else if(dauDates.length > 0){
      return (
        <>
        <Box mb={2}>
          <Text variant='body' color='gray-light'>
            Valid metrics available from {METRIC_VALID_START_DATE}. Highlighted values are the average for the past 30 days.
          </Text>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <MetricBox metric='DAU' dates={dauDates} dataset={dauDataset} highlightedValue={dauAverage}/>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <MetricBox metric='Impressions' dates={impressionsDates} dataset={impressionsDataset} highlightedValue={impressionsAverage}/>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <MetricBox metric='Daily Engagement' dates={dailyEngagementDates} dataset={dailyEngagementDataset} highlightedValue={dailyEngagementAverage}/>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <MetricBox metric='Unique Authors' dates={uniqueAuthorsDates} dataset={uniqueAuthorsDataset} highlightedValue={uniqueAuthorsAverage}/>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <MetricBox metric='Unique Posts' dates={uniquePostsDates} dataset={uniquePostsDataset} highlightedValue={uniquePostsAverage}/>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <MetricBox metric='Engaged Users' dates={engagedUsersDates} dataset={engagedUsersDataset} highlightedValue={engagedUsersAverage}/>
          </Grid>
        </Grid>
          {/* <Box mt={4} mb={2} sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ width: { xs: '100%', sm: '48%', md: '31%',lg: '18.5%' }}}>
              <MetricBox metric='DAU' dates={dauDates} dataset={dauDataset}/>
            </Box>
            <Box sx={{ width: { xs: '100%', sm: '48%', md: '31%',lg: '18.5%' }}}>
              <MetricBox metric='Impressions' dates={impressionsDates} dataset={impressionsDataset}/>
            </Box>
            <Box sx={{ width: { xs: '100%', sm: '48%', md: '31%',lg: '18.5%' }}}>
              <MetricBox metric='Daily Engagement' dates={dailyEngagementDates} dataset={dailyEngagementDataset}/>
            </Box>
            <Box sx={{ width: { xs: '100%', sm: '48%', md: '31%',lg: '18.5%' }}}>
              <MetricBox metric='Unique Authors' dates={uniqueAuthorsDates} dataset={uniqueAuthorsDataset}/>
            </Box>
            <Box sx={{ width: { xs: '100%', sm: '48%', md: '31%',lg: '18.5%' }}}>
              <MetricBox metric='Unique Posts' dates={uniquePostsDates} dataset={uniquePostsDataset}/>
            </Box>
          </Box>  */}
        </>
      )
    }
    else {
      return <></>
    }
  }

  const PromotionListDisplay = () => {
    return (
      <Box mt={2}>

        <Stack direction='row' spacing={2} alignItems={'center'} mb={4}>
          <Text variant='body' color='gray-lighter' sx={{ my:4 }}>
            {`You can add up to ${MAX_FEED_PROMOTIONS} feeds as promotions, with total percentage not more than 100%.  You can also add promotion items at specific positions in the feed.`}
          </Text>
          { !showAddPromotionSection &&
            <PrimaryButton 
              onClick={() => {
              setPromotionName('');
              setSelectedPromotionFeedId('');
              setSelectedPromotionFeedName('');
              setPromotionPercentage(20);
              setShowAddPromotionSection(true);
              setPromotionEditIndex(-1);
              if(promotionList.length >= MAX_FEED_PROMOTIONS){
                setPromotionTypeValue('items');
              }
            }}>Add</PrimaryButton>             
          }
        </Stack>
        {
          promotionList.length > 0 &&
          <Box>
            <Box mt={2}>
              <Text variant='h3'>Feeds</Text>
            </Box>
            {promotionList.map((promotion, index) => {
                const isEditingStyles = promotionEditIndex === index ? {
                  backgroundColor: colors['gray-lightest'],
                  mx: -2,
                  p: 2,
                  borderRadius: 1
                } : {};
                return (
                  <Stack direction='row' spacing={2} alignItems={'center'} my={1} sx={{minHeight: 40, ...isEditingStyles}}>
                    <Stack direction='row' spacing={1} flex={1}>
                      <Text>{`${index + 1}.`}</Text>
                      <Text variant='small' sx={{backgroundColor: colors['gray-lightest'], padding: '2px 4px', borderRadius: 1}}>{`${promotion.percent}%`}</Text>  
                      <Text>{`${promotion.promotion_name} (${promotion.feed_id})`}</Text>
                      <Stack direction='row' alignItems={'center'}>
                        <IconButton sx={{width: 14, height: 14}}>
                          <SkewIcon sx={{fontSize: 12, color: indigo[300]}} />
                        </IconButton>
                        <Text color='indigo'>{`${promotion.skew}`}</Text>
                      </Stack>
                    </Stack>
                      <Stack direction='row' alignItems={'center'} alignSelf={'flex-end'}>
                        <IconButton alignSelf={'flex-end'} onClick={() => {
                          setPromotionTypeValue('feed');
                          setSelectedPromotionFeedId(promotion.feed_id);
                          setPromotionName(promotion.promotion_name);
                          setPromotionPercentage(promotion.percent);
                          setPromotionEditIndex(index);
                          promotionRef.current.scrollIntoView({ behavior: 'smooth' });
                          setShowAddPromotionSection(true);
                        }} sx={{width: 40, marginRight: 1}}>
                          <EditIcon/>
                        </IconButton>
                        <IconButton alignSelf={'flex-end'} onClick={() => {setPromotionList(promotionList.filter((_, i) => i !== index))}} sx={{width: 40, marginRight: 1}}>
                          <RemoveIcon />
                        </IconButton>  
                      </Stack>

                    
                  </Stack>
                )
              })
            }
          </Box>
        }
        {
          itemsList.length > 0 &&
          <Box>
            <Box mt={2}>
              <Text variant='h3'>Items</Text>
            </Box>
            <Box mt={2}>
              <Stack direction='row' spacing={4}>
                <Box>
                  <Text variant='fieldHeading' color='brand-light'> Item ID </Text>
                  {
                    itemsList.map((item) => <Box mt={1}><Text variant='fieldValue' color='gray-light'> {item.item_id} </Text></Box>)
                  }
                </Box>
                <Box ml={4}>
                  <Text variant='fieldHeading' color='brand-light'> Rank </Text>
                  {
                    itemsList.map((item) => <Box mt={1}><Text variant='fieldValue' color='gray-light'> {item.rank} </Text></Box>)
                  }
                </Box>
              </Stack>
            </Box>          
          </Box>
          
        }
  
      </Box>
      
    )

  }

  const DynamicAILabelDialog = ( {handleAddDynamicAILabel}) => {
      const [type, setType] = useState('current');
      const [aiLabelSet, setAiLabelSet] = useState('mbd_ai_labels_topics');
      const [userOptions, setUserOptions] = useState([]);
      const [selectedUser, setSelectedUser] = useState('');
      const aiLabelSetOptions = showTbaOptions ? ['mbd_ai_labels_topics', 'tba_taxonomy_v1'] : ['mbd_ai_labels_topics'];

      return (
          <Dialog open={dynamicAILabelDialogOpen}>
              <DialogTitle>
                  <Stack px={2} direction={'row'} my={2} alignItems={'center'} justifyContent={'space-between'}>
                  Add a Dynamic AI Label
                  <Avatar src={logo_image} alt="Company Logo" style={{width: 35, height: 35}}/>
                  </Stack>
              </DialogTitle>
              <DialogContent>
                  <Box mb={2} mx={4}>
                      <Text variant='body2' color='gray-light'>
                        Dynamic AI labels are labels that vary by context.  Here, you can let the system decide the appropriate labels for the specified user.
                      </Text>
                      <Box mt={2}>
                        <Text variant='fieldHeading' color='brand-light'>
                          Type
                        </Text>
                      </Box>
                      <Box mt={2} p={2} sx={{border: `1px solid ${colors['gray-lightest']}`, borderRadius: 2}}>
                        <Stack direction='row' spacing={1} alignItems={'center'}>
                            <Radio
                              checked={type === 'current'}
                              onChange={e => setType(e.target.value)}
                              value="current"
                              name="radio-buttons"
                              inputProps={{ 'aria-label': 'feed' }}
                            />
                            <Text>Current User</Text>
                            <Radio
                              checked={type === 'specified'}
                              onChange={e => setType(e.target.value)}
                              value="specified"
                              name="radio-buttons"
                              inputProps={{ 'aria-label': 'items' }}
                            />
                            <Text>Specify A User</Text>
                        </Stack>                    
                        {
                          type === 'specified' &&
                          <Autocomplete
                            options={userOptions}
                            value={selectedUser}
                            onInputChange={async (e, newValue) => {
                              if (newValue) {
                                const options = await fetchAutocompleteOptions(newValue, 'user_ids');
                                setUserOptions(options);
                              }
                            }}
                            onChange={(e, newValue) => {
                                setSelectedUser(newValue);
                            }}
                            renderInput={(params) => <TextField {...params} variant="outlined" />}
                            sx={{ mb: 2 }}
                          />
                        }
                      </Box>
                      {
                        showTbaOptions &&
                        <Box mt={2}>
                          <SelectField heading="Label Set" value={aiLabelSet} onChange={e => setAiLabelSet(e.target.value)} options={aiLabelSetOptions.map(option => ({label: option, value: option}))} width='350px'/>
                        </Box>

                      }

                  </Box>
              </DialogContent>
              <DialogActions sx={{justifyContent: 'center', mb: 3}}>
                  <SecondaryButton onClick={() => setDynamicAILabelDialogOpen(false)}>Cancel</SecondaryButton>
                  <PrimaryButton onClick={() => {
                      const user = type === 'current' ? 'current_user' : selectedUser;
                      const newLabelDisplayValue = type === 'current' ? {label: 'current_user', value: 'user_id'} : selectedUser;
                      console.log(newLabelDisplayValue);
                      handleAddDynamicAILabel(newLabelDisplayValue,aiLabelSet);
                      setDynamicAILabelDialogOpen(false);
                  }}>Add</PrimaryButton>
              </DialogActions>
          </Dialog>
      )
  }
  const ThresholdDialog = ( {handleAddThreshold}) => {
    const operatorOptions = ['>=', '<=', '==', '!=', '>', '<'];
    const labelOptions = [{label: 'spam', value: 'spam'}];  // only supporting spam for now
    const [labelName, setLabelName] = useState('spam');
    const [labelThreshold, setLabelThreshold] = useState(0);
    const [operator, setOperator] = useState('>=');
    const [conditionValue, setConditionValue] = useState(0);
    const [includeCondition, setIncludeCondition] = useState(false);

    return (
        <Dialog open={thresholdDialogOpen}>
            <DialogTitle>
                <Stack px={2} direction={'row'} my={2} alignItems={'center'} justifyContent={'space-between'}>
                Add a Custom AI Label Threshold
                <Avatar src={logo_image} alt="Company Logo" style={{width: 35, height: 35}}/>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Box mb={2} mx={4}>
                    <Text variant='body2' color='gray-light'>
                      Set a custom threshold for an AI label.  Optionally define a condition to apply the threshold to. (We're only supporting this feature for the "spam" label at the moment, but will add support for other labels soon.)
                    </Text>
                    <Stack direction='row' mt={2}spacing={2} alignItems={'center'}>
                    <SelectField heading="AI Label" value={labelName} onChange={e => setLabelName(e.target.value)} options={labelOptions} width='350px'/>
                    <CountField step={0.01} isDecimal={true} heading="Threshold" value={labelThreshold} onChange={(e) => setLabelThreshold(e.target.value)} />
                    </Stack>

                    <Stack direction='row' spacing={1} alignItems={'center'} my={2}>
                      <Checkbox checked={includeCondition} onChange={e => setIncludeCondition(e.target.checked)} />
                      <Text>Include a condition for which to apply this custom threshold</Text>
                    </Stack>
                      {
                        includeCondition &&
                        <Stack direction='row' spacing={2} alignItems={'center'}>
                          <SelectField heading="Condition" value={'interaction_count'} options={[{label: 'interaction_count', value: 'interaction_count'}]} width='260px'/>
                          <SelectField heading=" . " value={operator} onChange={e => setOperator(e.target.value)} options={operatorOptions.map(option => ({label: option, value: option}))} />
                          <CountField heading=" . " value={conditionValue} onChange={e => setConditionValue(e.target.value)}/>
                        </Stack>
                      }

                </Box>
            </DialogContent>
            <DialogActions sx={{justifyContent: 'center', mb: 3}}>
                <SecondaryButton onClick={() => setThresholdDialogOpen(false)}>Cancel</SecondaryButton>
                <PrimaryButton onClick={() => {
                    handleAddThreshold(
                      labelName, 
                      labelThreshold, 
                      includeCondition ? {
                        feature: 'interaction_count',
                        operator: operator,
                        value: conditionValue
                      } : {});
                    setThresholdDialogOpen(false);
                }}>Add</PrimaryButton>
            </DialogActions>
        </Dialog>
    )
}


  return (
    <Box>
      <Box p={4}>
      <Box mb={4} width='50%' minWidth='300px'>
        <Typography variant="body2" color="textSecondary">
          Select a feed (type to search)
        </Typography>
        <Stack direction='row' spacing={2} alignItems={'center'}>
          <FeedSelector feeds={privateFeeds} selectedFeedId={feedId} onChange={(newValue) => {
            setFeedId(newValue.config_id);
            handleFeedSelection({ target: { value: newValue.config_id } });
          }}/>
          <PrimaryButton onClick={handleCreateNewFeed} sx={{width: '120px'}}>New Feed</PrimaryButton>
        </Stack>
      </Box>
      
      <Card 
        title={feedName}
        description={description === '' ? '(no description)' : description}
        icon={<Stack direction={'row'} alignItems={'end'} spacing={1} mr={8} sx={{marginBottom: 30}} height={'100%'}><Text variant='body'>{feedId}</Text><EndpointTag endpoint={endpoint}/></Stack>}
        controls={<FeedEditControls />}
      >
        { isEditMode ?
          <Box>
            <Box display="flex" mt={2} fullWidth>
              <Box flex={1}>
                <NonEditableTextField horizontal={true} heading="Feed ID" value={isNewFeed ? '(feed_id generated when saved)' : feedId} showExplanation={true} explanationTitle={explanations.feedId.title} explanationDescription={explanations.feedId.description} sx={{width: 100}}/>
              </Box>
              <Box flex={1}>
                <EditableTextField horizontal={true} heading="Feed Name" value={feedName} onChange={(e) => setFeedName(e.target.value)}/>
              </Box>
            </Box>
            <Box display="flex" mt={2}>
              <SelectField heading="Endpoint" value={endpoint} onChange={handleEndpointChange} options={endpointOptions.map(option => ({label: option, value: option}))} width='350px'/>
            </Box>
            <Box mt={2}>
              <EditableTextField horizontal={false} multiline={true} rows={4} heading="Description" value={description} onChange={(e) => setDescription(e.target.value)}/>
            </Box>    
          </Box>
          :
          <MetricsArea />
        }
              
      </Card>
      </Box>
      
    <Grid container justifyContent="center" spacing={4} px={4} >
      <BrandedDialog 
        open={openInfoDialog} 
        title={infoDialogTitle} 
        texts={infoDialogText} 
        onClose={() => setOpenInfoDialog(false)} 
        buttonText="Got It 👀 "
      />
      <BrandedDialog 
        open={feedId && feedId !== '' ? showCodeDialog : false} 
        title={feedId && feedId !== '' ? `${feedName} (${feedId}) - Code to get feed` : ''} 
        jsx={
          <Box>
            <Stack direction='row' alignItems='center' justifyContent='space-between' mb={4}>
              <Text variant='body'>
                Copy and paste the code below and run in your terminal to see the feed in action.  Your API key is included in the copied code.
              </Text>
              <IconButton onClick={copyCode} sx={{width: 22, height: 22, ml: 2}}>
                <Tooltip title="Copy code">
                  <CopyIcon />
                </Tooltip>
              </IconButton>
            </Stack>
            <SyntaxHighlighter language="bash" style={solarizedDarkAtom} showLineNumbers customStyle={{fontSize: '13px', 
              overflowX: 'auto'}}>
              {codeString}
            </SyntaxHighlighter>
          </Box>
        } 
        wide={true}
        onClose={() => setShowCodeDialog(false)} 
      />

      <Grid item xs={12} md={isShowcaseMode ? 12 : 6} sm={isShowcaseMode ? 12 : 8} pr={isShowcaseMode ? 0 : 2}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <Loader />
          </Box>
        ) : (
          <>

          {
            isShowcaseMode ? <FeedTemplateShowcase 
              publicFeeds={publicFeeds}
              privateFeeds={privateFeeds}
              createNewFeed={createNewFeed}
              selectTemplate={selectTemplate}
              editFeed={editFeed}
              /> 
            : 
            <Box>

              { !isEditMode ? <NonEditableFeedDetails />  :
                  <>

                    <Card 
                      title='Step 1: Candidate Generation'
                      description='Set filters to prepare a list of candidate recommendations to be shown to the user.'
                    >
                      <Box display="flex" alignItems="center" gap={1} mt={4}>
                        <CountField id='time-period-field' horizontal minWidth={100} heading="Time Period" value={timePeriod} onChange={(e) => setTimePeriod(e.target.value)} sx={{ width: '20px' }}/>
                        <Select variant='standard' value={timePeriodUnit} onChange={(e) => setTimePeriodUnit(e.target.value)} sx={{ml: 2}}>
                          <MenuItem value="days">days</MenuItem>
                          <MenuItem value="hours">hours</MenuItem>
                        </Select>
                      </Box>

                      {/* Include Params Section */}
                      <Box display="flex" alignItems="center" gap={2} mt={6}>
                        <Text variant='h2'>Include Params</Text>
                        {
                          isEditMode &&
                          <Button onClick={handleIncludeParamsDialog}>
                            <Tooltip title="Add or remove inclusion rules">
                              <EditIcon sx={{color:primaryColor}}/>
                            </Tooltip>
                          </Button>
                        }
                      </Box>

                      <Dialog open={openIncludeDialog} onClose={() => setOpenIncludeDialog(false)}>
                        <DialogTitle>Select Include Params</DialogTitle>
                        <DialogContent>
                          {['app_fids', 'geo_locations', 'author_ids', 'channels', 'ai_labels', 'embed_domains', 'publication_types', 'languages'].map((param) => (
                            <FormControlLabel
                              key={param}
                              control={<Checkbox checked={selectedIncludeParams.includes(param)} onChange={handleIncludeParamsChange} name={param} />}
                              label={param}
                              disabled={visibility === 'public'} // Disable in public mode
                            />
                          ))}
                          {
                            authorIdWarningVisible &&
                            <Stack direction={'row'} spacing={1} mt={1} alignItems={'center'}>
                              <InfoIcon sx={{color:colors['gray-lighter']}}/>
                              <Text variant='warning' color='gray-lighter'>
                                Note that author_ids in exclude params is ignored if author_ids is set here.
                              </Text>
                            </Stack>
                          }
                          {
                            geoLocationWarningVisible &&
                            <Stack direction={'row'} spacing={1} mt={1} alignItems={'center'}>
                              <InfoIcon sx={{color:colors['gray-lighter']}}/>
                              <Text variant='warning' color='gray-lighter'>
                                Note that geo_locations in exclude params is ignored if geo_locations is set here.
                              </Text>
                            </Stack>
                          }
                        </DialogContent>
                        <DialogActions>
                          <Button onClick={() => setOpenIncludeDialog(false)}>Close</Button>
                        </DialogActions>
                      </Dialog>
                      <Dialog open={openLabelOptionsDialog} onClose={() => setOpenLabelOptionsDialog(false)}>
                        <DialogTitle>Set AI Label Threshold</DialogTitle>
                        <DialogContent>
                          <NonEditableTextField horizontal heading="AI Label" value={currentAiLabel}/>
                          <NonEditableTextField horizontal heading="Threshold" value={aiLabelThresholds[currentAiLabel] || '(not set - using mbd default)'} valueNotSet={ aiLabelThresholds[currentAiLabel] ? false : true}/>
                          {/* <CountField horizontal heading="Threshold" value={aiLabelThresholds[currentAiLabel] || 0} onChange={(e, value) => setAiLabelThresholds(prev => ({ ...prev, [currentAiLabel]: value }))} step={0.01}/> */}
                          <Box px={2} mt={4}>
                          <Slider
                            value={currentAiLabelThreshold || 0} // Use the stored threshold value or default to 0
                              onChange={(e, value) => {
                                setCurrentAiLabelThreshold(value); 
                                setAiLabelThresholds(prev => ({ ...prev, [currentAiLabel]: value })) } }
                            step={0.01}
                            min={0}
                            max={1}
                            valueLabelDisplay="auto" // Display value on the slider
                            sx={{ color: aiLabelThresholds[currentAiLabel] ? colors['brand-light'] : colors['gray-lighter'], minWidth: 400 }}
                          />
                          </Box>
                        </DialogContent>
                        <DialogActions>
                          <Button  sx={{ color: primaryColor }} onClick={() => setOpenLabelOptionsDialog(false)} >OK</Button>
                        </DialogActions>
                      </Dialog>


                      <Box my={2}>
                        {selectedIncludeParams.includes('app_fids') && (
                            <Box mt={4}>
                              <Text variant='fieldHeading' color='brand-light'>
                                Source App (type App FID and press enter to add)
                              </Text>
                              <Autocomplete
                                multiple
                                freeSolo
                                options={appFidOptions}
                                value={appFids}
                                onChange={(e, newValue) => setAppFids(newValue)}
                                renderInput={(params) => <TextField {...params} variant="outlined" />}
                                sx={{ mt: 2 }}
                              />
                            </Box>
                        ) }
                        {selectedIncludeParams.includes('geo_locations') && (
                            <Box mt={4}>
                              <Text variant='fieldHeading' color='brand-light'>
                                Geo Locations
                              </Text>
                              <Autocomplete
                                multiple
                                freeSolo
                                options={geoLocations.map((location) => location.location.split('geo:')[1] + ' (' + getLocationName(location) + ')')}
                                value={selectedGeoLocations}
                                onChange={(e, newValue) => setSelectedGeoLocations(newValue)}
                                renderInput={(params) => <TextField {...params} variant="outlined" />}
                                sx={{ mt: 2 }}
                              />
                            </Box>
                        ) }
                        {selectedIncludeParams.includes('author_ids') && (
                            <Box mt={4}>
                              <Text variant='fieldHeading' color='brand-light'>
                                Author Ids
                              </Text>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={includeAuthorIds.some((item) => item.value === 'user/user_id/following')}
                                    onChange={(e) => {
                                      const hasFollowing = includeAuthorIds.some((item) => item.value === 'user/user_id/following');

                                      if (e.target.checked) {
                                        if (!hasFollowing) {
                                          // Add "following" entry if not present
                                          setIncludeAuthorIds([{ label: 'following', value: 'user/user_id/following' }]);
                                          setSnackbarMessage('Only one entry for this type of author_ids are allowed at a time');
                                          setShowSnackbar(true);
                                        }
                                      } else {
                                        // Remove "following" entry
                                        setIncludeAuthorIds((prev) => prev.filter((item) => item.value !== 'user/user_id/following'));
                                      }
                                                                  
                                    }}
                                  />
                                }
                                label="following (include Author IDs which user is following)"
                                sx={{ mt:1}}
                              />
                              <Autocomplete
                                multiple
                                options={authorOptions}
                                value={includeAuthorIds}
                                onInputChange={async (e, newValue) => {
                                  if (newValue) {
                                    const options = await fetchAutocompleteOptions(newValue, 'author_ids');
                                    setAuthorOptions(options);
                                  }
                                }}
                                onChange={(e, newValue) => {
                                  const hasFollowing = newValue.some((item) =>
                                    item.value.includes('following')
                                  );
                                  if (hasFollowing) {
                                    // Allow only one entry if "following" or "user_id:following" is selected
                                    setIncludeAuthorIds([newValue.find((item) => item.value.includes('following'))]);
                                    setSnackbarMessage('Only one entry for this type of author_ids are allowed at a time');
                                    setShowSnackbar(true);
                                  } else {
                                    // Allow multiple entries otherwise
                                    setIncludeAuthorIds(newValue);
                                  }
                                }}
                                renderInput={(params) => <TextField {...params} variant="outlined" />}
                                sx={{ mb: 2 }}
                              />
                              {
                                includeAuthorIds.length > 0 &&
                                <Stack direction='row' spacing={2} sx={{ mt:4 }} alignItems={'center'}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={returnEngagedByAuthorIds} 
                                          onChange={(e) => setReturnEngagedByAuthorIds(e.target.checked)} 
                                          sx={{ '&.Mui-checked': { color: primaryColor }}} 
                                        />
                                      }
                                      label=""
                                    />
                                    <Text variant='fieldHeading' color='gray'> Return posts that the users listed above have interacted with</Text>
                                </Stack>
                              }
                            </Box>
                        )}
                        {selectedIncludeParams.includes('channels') && (
                            <Box mt={4}>
                              <Text variant='fieldHeading' color='brand-light'>
                                Channels
                              </Text>
                              <Autocomplete
                                multiple
                                options={channelOptions}
                                value={channels}
                                onInputChange={async (e, newValue) => {
                                  if (newValue) {
                                    const options = await fetchAutocompleteOptions(newValue, 'channels');
                                    setChannelOptions(options);
                                  }
                                }}
                                onChange={(e, newValue) => setChannels(newValue)}
                                renderInput={(params) => <TextField {...params} variant="outlined" />}
                                sx={{ my: 2 }}
                              />
                            </Box>
                          
                        )}
                        {selectedIncludeParams.includes('ai_labels') && (
                            <>
                              <DynamicAILabelDialog handleAddDynamicAILabel={ (userObj, labelSet) => {
                                // const category = labelSet === 'mbd_ai_labels' ? 'topics': ''; 
                                const newLabel = `${userObj.label}:${labelSet}`;// + (category !== '' ? `_${category}` : ''); // mbd_ai_labels --> mbd_ai_labels_topics
                                const newValue = `user/${userObj.value}/${labelSet}`; // + (category !== '' ? `/${category}` : ''); // mbd_ai_labels --> mbd_ai_labels/topics
                                setIncludeAILABELS([...includesAILABELS, { label: newLabel, value: newValue }]);
                              }} />
                              
                              <Box mt={4}>
                                  <Text variant="fieldHeading" color='brand-light'>AI Labels</Text>

                                  <Box mt={2}>
                                    <SecondaryButton onClick={() => setDynamicAILabelDialogOpen(true)}>Add Dynamic AI Label</SecondaryButton>
                                  </Box>
                                  <Box mt={2}>
                                      <Autocomplete
                                        multiple
                                        options={groupedAILabelOptions}
                                        value={includesAILABELS}
                                        onChange={(e, newValue) => { 
                                          const newLabel = findNewItem(includesAILABELS, newValue); 
                                          setIncludeAILABELS(newValue);
                                          if (newLabel && newLabel.value) {
                                            setCurrentAiLabel(newLabel.value); 
                                            setCurrentAiLabelThreshold(aiLabelThresholds[newLabel.value] || 0);
                                            // setOpenLabelOptionsDialog(true);
                                          }
                                        }}
                                        groupBy={ (option) => option.set}
                                        getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                                        isOptionEqualToValue={(option, value) => 
                                          (typeof option === 'string' ? option : option.value) === (typeof value === 'string' ? value : value.value)
                                        }
                                        renderInput={(params) => <TextField {...params} variant="outlined" />}
                                        renderTags={(value, getTagProps) =>
                                          value.map((option, index) => {
                                            const { key, ...tagProps } = getTagProps({ index });
                                            const label = typeof option === 'string' ? option : option.label;
                                            return (
                                              <Chip disabled={(endpoint !== 'casts/feed/trending' && endpoint !== 'casts/feed/popular')} label={label} key={key} {...tagProps} 
                                                onClick={
                                                  (endpoint === 'casts/feed/for-you') ? 
                                                    () => { 
                                                      const optionValue = typeof option === 'string' ? option : option.value;
                                                      setCurrentAiLabel(optionValue); 
                                                      setCurrentAiLabelThreshold(aiLabelThresholds[optionValue] || 0);
                                                      // if (!label.startsWith('dynamic_for_user')) {
                                                      //   // setOpenLabelOptionsDialog(true)
                                                      // }
                                                    }
                                                  : () => {}
                                                }
                                                deleteIcon={
                                                  <CancelIcon
                                                    onMouseDown={
                                                      (event) => handleDeleteIncludeAILabel(event, label)
                                                    }
                                                  />
                                                }  
                                              />
                                            );
                                          })
                                        }
                                
                                        sx={{ my: 2 }}
                                      />


                                  </Box>
                              </Box>
                            </>
                        )}
                        {selectedIncludeParams.includes('embed_domains') && (

                            <Box mt={4}>
                            <Text variant='fieldHeading' color='brand-light'>
                              Embed Domains (press enter to add domain not in suggested list)
                            </Text>
                            <Autocomplete
                                    multiple
                                    freeSolo
                                    options={embedDomainsOptions}
                                    value={embedDomains}
                                    onChange={(e, newValue) => setEmbedDomains(newValue)}
                                    renderInput={(params) => <TextField {...params} variant="outlined" />}
                                    sx={{ my: 2 }}
                            />
                            </Box>

                        )}
                        {selectedIncludeParams.includes('publication_types') && (
                          <Box mt={4}>
                            <Text variant='fieldHeading' color='brand-light'>
                              Publication Types
                            </Text>
                            <Autocomplete
                                multiple
                                options={publicationTypesOptions.map(option => ({label: option, value: option}))}
                                value={includePublicationTypes}
                                onChange={(e, newValue) => setIncludePublicationTypes(newValue)}
                                renderInput={(params) => <TextField {...params} variant="outlined" />}
                                sx={{ my: 2 }}
                              />
                            </Box>
                        )}
                        {
                          selectedIncludeParams.includes('publication_types') && includePublicationTypes.map((type) => type.value).includes('video') && (
                            <Box>
                              <Box mt={4}>
                                <MinMaxInput heading="Video Duration (s)" minWidth={20} min={minVideoDuration} max={maxVideoDuration} setMin={setMinVideoDuration} setMax={setMaxVideoDuration}/>
                              </Box>
                              <Text variant='small' color='gray-light'>values of 0 are ignored</Text>
                              <Box mt={4}>
                                <Text variant='fieldHeading' color='brand-light'>
                                  Video Orientations
                                </Text>
                                <Autocomplete
                                  multiple
                                  options={videoOrientationOptions}
                                  value={includeVideoOrientations}
                                  onChange={(e, newValue) => setIncludeVideoOrientations(newValue)}
                                  renderInput={(params) => <TextField {...params} variant="outlined" />}
                                  sx={{ my: 2 }}
                                />
                              </Box>
                              <Box mt={4}>
                                <Text variant='fieldHeading' color='brand-light'>
                                  Video Languages
                                </Text>
                                <Autocomplete
                                  multiple
                                  options={videoLanguageOptions}
                                  value={includeVideoLanguages}
                                  onChange={(e, newValue) => setIncludeVideoLanguages(newValue)}
                                  renderInput={(params) => <TextField {...params} variant="outlined" />}
                                  sx={{ my: 2 }}
                                />
                              </Box>
                            </Box>
                          )
                        }
                        {
                          selectedIncludeParams.includes('publication_types') && includePublicationTypes.map((type) => type.value).includes('miniapp') && (
                            <Box mt={4}>
                            <Text variant='fieldHeading' color='brand-light'>
                              Miniapp Categories
                            </Text>
                            <Autocomplete
                              multiple
                              options={miniappCategoriesOptions}
                              value={includeMiniappCategories}
                              onChange={(e, newValue) => setIncludeMiniappCategories(newValue)}
                              renderInput={(params) => <TextField {...params} variant="outlined" />}
                              sx={{ my: 2 }}
                            />
                          </Box>
                          )
                        }
                        {selectedIncludeParams.includes('languages') && (
                          <Box mt={4}>
                            <Text variant='fieldHeading' color='brand-light'>
                              Languages
                            </Text>
                            <Autocomplete
                                multiple
                                options={languages}
                                value={includeLanguages}
                                onChange={(e, newValue) => setIncludeLanguages(newValue)}
                                renderInput={(params) => <TextField {...params} variant="outlined" />}
                                sx={{ my: 2 }}
                              />
                            </Box>
                        )}
                        {
                          !selectedIncludeParams.includes('author_ids') &&
                          !selectedIncludeParams.includes('channels') &&
                          !selectedIncludeParams.includes('ai_labels') &&
                          !selectedIncludeParams.includes('embed_domains') &&
                          !selectedIncludeParams.includes('publication_types') &&
                          !selectedIncludeParams.includes('languages') &&

                          <Text variant='body' color='gray-lighter' sx={{ my:4 }}>
                            Not configured
                          </Text>
                          
                        }
                      </Box>

                      {/* Exclude Params Section */}
                      <Box display="flex" alignItems="center" gap={2} mt={4}>
                        <Text variant='h2'>Exclude Params</Text>
                        {
                          isEditMode &&
                          <Button onClick={handleExcludeParamsDialog}>
                            <Tooltip title="Add or remove exclusion rules">
                              <EditIcon sx={{color:primaryColor}}/>
                            </Tooltip>
                          </Button> 
                        }
                      </Box>

                      <Dialog open={openExcludeDialog} onClose={() => setOpenExcludeDialog(false)}>
                        <DialogTitle>Select Exclude Params</DialogTitle>
                        <DialogContent>
                          {['author_ids', 'ai_labels', 'geo_locations', 'channels', 'author_ai_labels'].map((param) => (
                            <FormControlLabel
                              key={param}
                              control={<Checkbox checked={selectedExcludeParams.includes(param)} onChange={handleExcludeParamsChange} name={param} />}
                              label={param}
                              disabled={visibility === 'public'} // Disable in public mode
                            />
                          ))}
                          {
                            authorIdWarningVisible &&
                            <Stack direction={'row'} spacing={1} mt={1} alignItems={'center'}>
                              <InfoIcon sx={{color:colors['gray-lighter']}}/>
                              <Text variant='warning' color='gray-lighter'>
                                Note that author_ids here is ignored if author_ids is set in include params.
                              </Text>
                            </Stack>
                          }
                          {
                            geoLocationWarningVisible &&
                            <Stack direction={'row'} spacing={1} mt={1} alignItems={'center'}>
                              <InfoIcon sx={{color:colors['gray-lighter']}}/>
                              <Text variant='warning' color='gray-lighter'>
                                Note that geo_locations here is ignored if geo_locations is set in include params.
                              </Text>
                            </Stack>
                          }
                        </DialogContent>
                        <DialogActions>
                          <Button onClick={() => setOpenExcludeDialog(false)}>Close</Button>
                        </DialogActions>
                      </Dialog>
                      <Dialog open={openExcludeLabelOptionsDialog} onClose={() => setOpenExcludeLabelOptionsDialog(false)}>
                        <DialogTitle>Set AI Label Threshold</DialogTitle>
                        <DialogContent>
                          <NonEditableTextField horizontal heading="AI Label" value={currentAiLabel}/>
                          <NonEditableTextField horizontal heading="Threshold" value={removeAILabelThresholds[currentAiLabel] || '(not set - using mbd default)'} valueNotSet={ removeAILabelThresholds[currentAiLabel] ? false : true}/>
                          <Box px={2} mt={4}>
                          <Slider
                            value={currentAiLabelThreshold || 0} // Use the stored threshold value or default to 0
                              onChange={(e, value) => {
                                setCurrentAiLabelThreshold(value); 
                                setRemoveAILabelThresholds(prev => ({ ...prev, [currentAiLabel]: value })) } }
                            step={0.01}
                            min={0}
                            max={1}
                            valueLabelDisplay="auto" // Display value on the slider
                            sx={{ color: removeAILabelThresholds[currentAiLabel] ? colors['brand-light'] : colors['gray-lighter'], minWidth: 400 }} // Display value on the slider
                          />
                          </Box>
                        </DialogContent>
                        <DialogActions>
                          <Button  sx={{ color: primaryColor }} onClick={() => setOpenExcludeLabelOptionsDialog(false)} >OK</Button>
                        </DialogActions>
                      </Dialog>

                      <Box my={2}>
                        {selectedExcludeParams.includes('author_ids') && (
                          <Box mt={4}>
                              <Text variant='fieldHeading' color='brand-light'>
                                Exclude Author Ids
                              </Text>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={excludeAuthorIds.some((item) => item.value === 'user/user_id/following')}
                                    onChange={(e) => {
                                      const hasFollowing = excludeAuthorIds.some((item) => item.value === 'user/user_id/following');

                                      if (e.target.checked) {
                                        if (!hasFollowing) {
                                          // Add "following" entry if not present
                                          setExcludeAuthorIds([{ label: 'following', value: 'user/user_id/following' }]);
                                          setSnackbarMessage('Only one entry for this type of author_ids are allowed at a time');
                                          setShowSnackbar(true);
                                        }
                                      } else {
                                        // Remove "following" entry
                                        setExcludeAuthorIds((prev) => prev.filter((item) => item.value !== 'user/user_id/following'));
                                      }
                                    }}
                                  />
                                }
                                label="following (exclude Author IDs which user is following)"
                                sx= {{ mt:1 }}
                              />
                              <Autocomplete
                                multiple
                                options={authorOptions}
                                value={excludeAuthorIds}
                                onInputChange={async (e, newValue) => {
                                  if (newValue) {
                                    const options = await fetchAutocompleteOptions(newValue, 'author_ids');
                                    setAuthorOptions(options);
                                  }
                                }}
                                onChange={(e, newValue) =>{
                                  const hasFollowing = newValue.some((item) =>
                                    item.value.includes('following')
                                  );
                                  if (hasFollowing) {
                                    // Allow only one entry if "following" or "user_id:following" is selected
                                    setExcludeAuthorIds([newValue.find((item) => item.value.includes('following'))]);
                                    setSnackbarMessage('Only one entry for this type of author_ids are allowed at a time');
                                    setShowSnackbar(true);
                                  } else {
                                    // Allow multiple entries otherwise
                                    setExcludeAuthorIds(newValue);
                                  }
                                }}
                                renderInput={(params) => <TextField {...params} variant="outlined" />}
                                sx={{ mb: 2 }}
                              />
                          </Box>

                        )}
                        {selectedExcludeParams.includes('ai_labels') && (
                            <>
                                  <Text variant="fieldHeading" color='brand-light'>AI Labels</Text>
                                  <Autocomplete
                                    multiple
                                    options={groupedAILabelOptions}
                                    value={excludeAILABELS}
                                    onChange={(e, newValue) => { 
                                      const newLabel = findNewItem(excludeAILABELS, newValue); 
                                      setExcludeAILABELS(newValue);
                                      if (newLabel && newLabel.value) {
                                        setCurrentAiLabel(newLabel.value); 
                                        setCurrentAiLabelThreshold(removeAILabelThresholds[newLabel.value] || 0);
                                        // setOpenExcludeLabelOptionsDialog(true);
                                      }
                                    }}
                                    groupBy={ (option) => option.set}
                                    getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                                    isOptionEqualToValue={(option, value) => 
                                      (typeof option === 'string' ? option : option.value) === (typeof value === 'string' ? value : value.value)
                                    }
                                    renderInput={(params) => <TextField {...params} variant="outlined" />}
                                    renderTags={(value, getTagProps) =>
                                      value.map((option, index) => {
                                        const { key, ...tagProps } = getTagProps({ index });
                                        const label = typeof option === 'string' ? option : option.label;
                                        return (
                                          <Chip disabled={(endpoint !== 'casts/feed/trending' && endpoint !== 'casts/feed/popular')} label={label} key={key} {...tagProps} 
                                            onClick={
                                              (endpoint === 'casts/feed/trending' || endpoint === 'casts/feed/popular') ? 
                                                  () => { 
                                                    const optionValue = typeof option === 'string' ? option : option.value;
                                                    setCurrentAiLabel(optionValue); 
                                                    setCurrentAiLabelThreshold(removeAILabelThresholds[optionValue] || 0);
                                                    // setOpenExcludeLabelOptionsDialog(true)
                                                  }
                                                : () => {}
                                            } 
                                            deleteIcon={
                                              <CancelIcon
                                                onMouseDown={
                                                  (event) => handleDeleteExcludeAILabel(event, label)
                                                }
                                              />
                                            }                                            
                                          />
                                        );
                                      })
                                    }
                            
                                    sx={{ my: 2 }}
                                  />

                            </>
                        )}
                        {selectedExcludeParams.includes('geo_locations') && (
                            <Box mt={4}>
                              <Text variant='fieldHeading' color='brand-light'>
                                Geo Locations
                              </Text>
                              <Autocomplete
                                multiple
                                options={geoLocations.map((location) => location.location.split('geo:')[1] + ' (' + getLocationName(location) + ')')}
                                value={excludeGeoLocations}
                                onChange={(e, newValue) => setExcludeGeoLocations(newValue)}
                                renderInput={(params) => <TextField {...params} variant="outlined" />}
                                sx={{ mt: 2 }}
                              />
                            </Box>
                        ) }
                        {selectedExcludeParams.includes('channels') && (
                          <Box mt={4}>
                            <Text variant='fieldHeading' color='brand-light'>
                              Channels
                            </Text>
                            <Autocomplete
                                multiple
                                options={channelOptions}
                                value={excludeChannels}
                                onInputChange={async (e, newValue) => {
                                  if (newValue) {
                                    const options = await fetchAutocompleteOptions(newValue, 'channels');
                                    setChannelOptions(options);
                                  }
                                }}
                                onChange={(e, newValue) => setExcludeChannels(newValue)}
                                renderInput={(params) => <TextField {...params} variant="outlined" />}
                                sx={{ my: 2 }}
                              />                            
                          </Box>
                        )}
                        {selectedExcludeParams.includes('author_ai_labels') && (
                            <Box mt={4}>
                              <Text variant='fieldHeading' color='brand-light'>
                                Author AI Labels
                              </Text>
                              <Stack direction='row' spacing={2} mt={2}>
                                <Box flexGrow={1}>
                                  {
                                    customAiLabelThresholds.length === 0 &&
                                    <Text variant='body' color='gray-lighter'>
                                      No exclude author AI labels configured yet.
                                    </Text>
                                  }
                                </Box>

                                <SecondaryButton onClick={() => setThresholdDialogOpen(true)}>Add Threshold</SecondaryButton>
                              </Stack>
                              <ThresholdDialog handleAddThreshold={(labelName, labelThreshold, condition) => {
                                setCustomAiLabelThresholds([...customAiLabelThresholds, {[labelName]: { threshold: labelThreshold, condition: condition}}]);
                              }} />
                              <Box mt={2}>                              
                              {
                                customAiLabelThresholds.map((threshold, index) => {
                                  const labelName = Object.keys(threshold)[0];
                                  const thresholdValue = threshold[labelName].threshold;
                                  const condition = threshold[labelName].condition;
                                  const labelThresholdString = `${labelName}: ${thresholdValue} `;
                                  const conditionString = condition.feature ? `(${condition.feature} ${condition.operator} ${condition.value})` : '';
                                  return (
                                    <Stack direction='row' spacing={1} my={1} alignItems={'center'} minHeight={40}> 
                                      <Text>{`${index + 1}. `}</Text>
                                      <Text sx={{backgroundColor: colors['gray-lightest'], padding: '2px 4px', borderRadius: 1}}>{labelThresholdString}</Text>
                                      <Text sx={{flexGrow: 1}}>{conditionString}</Text>

                                        <IconButton alignSelf={'flex-end'} onClick={() => {setCustomAiLabelThresholds(customAiLabelThresholds.filter((_, i) => i !== index))}} sx={{width: 40, marginRight: 1}}>
                                          <RemoveIcon />
                                        </IconButton>  
                                      
                                    </Stack>
                                  )
                                })
                              }
                              </Box>
                            </Box>
                        ) }
                        {
                          !selectedExcludeParams.includes('author_ids') &&
                          !selectedExcludeParams.includes('ai_labels') &&
                          !selectedExcludeParams.includes('geo_locations') &&
                          !selectedExcludeParams.includes('channels') &&

                          <Text variant='body' color='gray-lighter' sx={{ my:4 }}>
                            Not configured
                          </Text>
                          
                        }

                      <Box display="flex" alignItems="center" gap={2} mt={4}>
                      <Text variant='h2' > Engagement</Text>
                        {
                          isEditMode &&
                          <Button onClick={() => {
                            setShowEngagementFilters(!showEngagementFilters);
                            }}>
                            <Tooltip title="Use feed engagement filters">
                              <EditIcon sx={{color:primaryColor}}/>
                            </Tooltip>
                          </Button>
                        }
                      </Box>
                      {showEngagementFilters ?
                        <Box mt={4}>
                          <MinMaxInput heading="Likes" minWidth={20} min={minLikeCount} max={maxLikeCount} setMin={setMinLikeCount} setMax={setMaxLikeCount}/>
                          <Box mt={1}>
                            <MinMaxInput heading="Comments" minWidth={20} min={minCommentCount} max={maxCommentCount} setMin={setMinCommentCount} setMax={setMaxCommentCount}/>
                          </Box>
                          <Box mt={1} mb={2}>
                            <MinMaxInput heading="Shares" minWidth={20} min={minShareCount} max={maxShareCount} setMin={setMinShareCount} setMax={setMaxShareCount}/>
                          </Box>
                        </Box>
                        :
                        <Text variant='body' color='gray-lighter' sx={{ my:4 }}>
                          Not configured
                        </Text>
                      }
                      {/* <Box display="flex" alignItems="center" gap={2} mt={4}>
                        <Text variant='h2' > AI Label Thresholds</Text>
                        {
                          isEditMode &&
                          <Button onClick={() => {
                            setShowAiLabelThresholds(!showAiLabelThresholds);
                            }}>
                            <Tooltip title="Define custom thresholds for AI labels">
                              <EditIcon sx={{color:primaryColor}}/>
                            </Tooltip>
                          </Button>
                        }
                        {
                          showAiLabelThresholds &&
                          <Box>
                            <SecondaryButton onClick={() => setThresholdDialogOpen(true)}>Add Threshold</SecondaryButton>
                            <ThresholdDialog handleAddThreshold={(labelName, labelThreshold, condition) => {
                              setCustomAiLabelThresholds([...customAiLabelThresholds, {[labelName]: { threshold: labelThreshold, condition: condition}}]);
                            }} />

                          </Box>
                        }

                      </Box>
                      {
                        
                          customAiLabelThresholds.length > 0 ?
                          <Box mt={2}>                              
                          {
                            customAiLabelThresholds.map((threshold, index) => {
                              const labelName = Object.keys(threshold)[0];
                              const thresholdValue = threshold[labelName].threshold;
                              const condition = threshold[labelName].condition;
                              const labelThresholdString = `${labelName}: ${thresholdValue} `;
                              const conditionString = condition.feature ? `(${condition.feature} ${condition.operator} ${condition.value})` : '';
                              return (
                                <Stack direction='row' spacing={1} my={1} alignItems={'center'} minHeight={40}> 
                                  <Text>{`${index + 1}. `}</Text>
                                  <Text sx={{backgroundColor: colors['gray-lightest'], padding: '2px 4px', borderRadius: 1}}>{labelThresholdString}</Text>
                                  <Text sx={{flexGrow: 1}}>{conditionString}</Text>
                                  {
                                    showAiLabelThresholds &&
                                    <IconButton alignSelf={'flex-end'} onClick={() => {setCustomAiLabelThresholds(customAiLabelThresholds.filter((_, i) => i !== index))}} sx={{width: 40, marginRight: 1}}>
                                      <RemoveIcon />
                                    </IconButton>  
                                  }
                                </Stack>
                              )
                            })
                          }
                          </Box>
                          :
                          <Text variant='body' color='gray-lighter' sx={{ my:4 }}>
                            Not configured
                          </Text>
                      }                         */}
                      
                      
                      </Box>

                    </Card>
                    <Card 
                      title='Step 2: Ranking'
                      description='Ranking impacts how the candidate lists of recommendations is shown to a user, it can be used to make sure we show the most relevant items for example.'
                      >
                      {
                        endpoint === 'casts/feed/for-you' &&
                        <Box display="flex" mt={4}>
                          <SelectField heading="Scoring" value={scoring} onChange={handleScoringChange} options={scoringOptions.map(option => ({label: option, value: option}))} width='350px'/>
                        </Box>
                      }
                      <Stack direction='row' spacing={2} mt={6} alignItems={'center'} fullWidth>
                        <Text variant='fieldHeading' color='brand-light' sx={{minWidth: 140}}>
                            Time Decay
                            <IconButton
                              ml={1}
                              onClick={() => {setOpenInfoDialog(true); 
                                setInfoDialogTitle(explanations.timeDecay.title); 
                                setInfoDialogText(explanations.timeDecay.description)
                              }}
                            >
                              <Tooltip title="What is time decay?">
                                <QuestionIcon sx={{color:colors['gray-lighter']}}/>
                              </Tooltip>
                            </IconButton>
                        </Text>

                        <Checkbox
                          checked={useTimeDecay}
                          onChange={(e) => setUseTimeDecay(e.target.checked)}
                          sx={{ '&.Mui-checked': { color: primaryColor }}}
                        />
                        {
                          useTimeDecay ? 
                            <Stack direction='row' alignItems={'center'} gap={2} fullWidth flexGrow={1}>
                              <Text variant='body'>{timeDecayValue}</Text>
                              <Slider
                                valueLabelDisplay='auto'
                                value={timeDecayValue}
                                onChange={(e, value) => setTimeDecayValue(value)}
                                min={0}
                                max={1}
                                step={0.01}
                                sx={{width: '100%', mx: isMobile ? 2 : 8}}
                              />
                            </Stack>
                          :
                          <Text variant='body' color='gray-light'>(not using time decay)</Text>
                        }
                      </Stack>
                    </Card>
                    <Card 
                      title='Step 3: Visibility Filters'
                      description="Control what's shown to the user to maintain a great user experience by putting safeguards in place."
                    >
                      {/* <Box display="flex" alignItems="center" gap={1} mt={4}> 
                        <CountField horizontal heading="Impression Count" minWidth={190} value={impressionCount} onChange={(e) => setImpressionCount(e.target.value)} showExplanation={true} explanationTitle={explanations.impressionCount.title} explanationDescription={explanations.impressionCount.description}/>
                      </Box> */}
                      <Stack direction='row' spacing={2} sx={{ mt:4 }} alignItems={'center'}>
                          <Text variant='fieldHeading' color='brand-light'>Show items user has already interacted with (hidden by default)</Text>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={showInteracted} 
                                onChange={(e) => setShowInteracted(e.target.checked)} 
                                sx={{ '&.Mui-checked': { color: primaryColor }}} 
                              />
                            }
                            label=""
                          />
                        
                      </Stack>
                      <Stack direction='row' spacing={2} sx={{ mt:4 }} alignItems={'center'}>
                        <Text variant='fieldHeading' color='brand-light'>Remove posts created by the current user</Text>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={removePostsFromCurrentUser} 
                                onChange={(e) => setRemovePostsFromCurrentUser(e.target.checked)} 
                                sx={{ '&.Mui-checked': { color: primaryColor }}} 
                              />
                            }
                            label=""
                          />
                      </Stack>
                      <Stack direction='row' spacing={2} sx={{ mt:4 }} alignItems={'center'}>
                        <Text variant='fieldHeading' color='brand-light'>Return only posts with social proof</Text>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={withSocialProofOnly} 
                                onChange={(e) => setWithSocialProofOnly(e.target.checked)} 
                                sx={{ '&.Mui-checked': { color: primaryColor }}} 
                              />
                            }
                            label=""
                          />
                      </Stack>
                      <Box display="flex" alignItems="center" gap={2} mt={4}>
                      <Text variant='h2' > Feed Diversity</Text>
                        {
                          isEditMode &&
                          <Button onClick={() => {
                            setShowFeedDiversitySection(!showFeedDiversitySection);
                            }}>
                            <Tooltip title="Use feed diversity configurations">
                              <EditIcon sx={{color:primaryColor}}/>
                            </Tooltip>
                          </Button>
                        }
                      </Box>
                      {showFeedDiversitySection && (
                        <Box mt={4}>
                          <Box display="flex" alignItems="center" gap={1} mt={4}> 
                            <CountField horizontal minWidth={400} heading="Maximum Posts from Same Author" value={maxPostsPerAuthor} onChange={(e) => setMaxPostsPerAuthor(e.target.value)} showExplanation={true} explanationTitle={explanations.maxPostsPerAuthor.title} explanationDescription={explanations.maxPostsPerAuthor.description}/>
                          </Box>
                          <Box display="flex" alignItems="center" gap={1} mt={4}> 
                            <CountField horizontal minWidth={400} heading="Minimum Distance Between Posts from Same Author" value={minDistanceBetweenPostsFromSameAuthor} onChange={(e) => setMinDistanceBetweenPostsFromSameAuthor(e.target.value)} showExplanation={true} explanationTitle={explanations.minDistanceBetweenPosts.title} explanationDescription={explanations.minDistanceBetweenPosts.description}/>
                          </Box>                          
                        </Box>
                      ) 
                      }
                      {
                        !showFeedDiversitySection &&
                        maxPostsPerAuthor <= 0 &&
                        minDistanceBetweenPostsFromSameAuthor <= 0 &&
                        <Text variant='body' color='gray-lighter' sx={{ my:4 }}>
                          Not configured
                        </Text>
                        
                      }
               
                    </Card>
                    <Card 
                      title='Step 4: Feed Construction'
                      description='Additional configurations to control '
                    >


                        <Box display="flex" alignItems="center" gap={2} mt={4}>
                          <Text variant='h2' >Fallback Configurations
                            <IconButton
                                ml={1}
                                onClick={() => {setOpenInfoDialog(true); 
                                  setInfoDialogTitle(explanations.fallback.title); 
                                  setInfoDialogText(explanations.fallback.description)
                                }}
                              >
                                <Tooltip title="What is Fallback Configuration?">
                                  <QuestionIcon sx={{color:colors['gray-lighter']}}/>
                                </Tooltip>
                            </IconButton>
                          </Text>
                          {
                            isEditMode &&
                            <Button onClick={() => {
                              setShowFallbackSection(!showFallbackSection);
                              if (!showFallbackSection) {
                                  // Reset fallback feed
                                  setFallbackFeedId('');
                              }
                              }}>
                              <Tooltip title="Add or remove fallback configurations">
                                <EditIcon sx={{color:primaryColor}}/>
                              </Tooltip>
                            </Button>
                          }
                        </Box>
                        {showFallbackSection ? (
                            <Box mt={4}>
                                {
                                  fallbackFeedList.length > 0 ?
                                    <Box>
                                      <Text variant='body' color='gray-lighter' sx={{ my:4 }}>
                                        A maximum of 3 fallback feeds cna be added.
                                      </Text>
                                      {
                                        fallbackFeedList.map((feedId, index) => (
                                          <Stack mt={1} direction='row' spacing={2} alignItems={'center'} key={'fallback-'+index}>
                                            <Stack flexGrow={1} direction='row' spacing={2} alignItems={'center'}>
                                              <Box mr={6}>
                                                <Text variant='body'>{index+1}.</Text>
                                              </Box>
                                              <NonEditableTextField horizontal={true} heading="Feed ID" value={feedId}/>
                                              <NonEditableTextField horizontal={true} heading="Feed Name" value={[...publicFeeds, ...privateFeeds].find((f) => f.config_id === feedId).name}/>
                                            </Stack>
                                            <IconButton alignSelf={'flex-end'} onClick={() => {setFallbackFeedList(fallbackFeedList.filter((_, i) => i !== index))}} sx={{width: 40, marginRight: 1}}>
                                                <RemoveIcon />
                                              </IconButton>  
                                          </Stack>
                                        ))                                        
                                      }
                                    </Box>

                                  :
                                    <Text variant='body' color='gray-lighter' sx={{ my:4 }}>
                                      No fallback feeds configured.  Select a feed below to be added to the fallback list.  A maximum of 3 fallback feeds is supported at the moment.
                                    </Text>
                                }
                                {
                                  fallbackFeedList.length < 3 &&
                                    <Stack direction='row' spacing={2} alignItems={'flex-end'} mt={4}>
                                      <Box width={'100%'}>
                                        <Text variant='fieldHeading' color='gray-light'>
                                          Select a feed to be added as fallback
                                        </Text>
                                        <FeedSelector feeds={privateFeeds.filter((f) => f.endpoint === endpoint)} templateFeeds={publicFeeds.filter((f) => f.endpoint === endpoint)} filterOutFeedIds={[feedId, ...fallbackFeedList]} selectedFeedId={fallbackFeedId} onChange={(newValue) => {
                                          setFallbackFeedId(newValue.config_id);
                                          setFallbackFeedName(newValue.name);
                                        }}/>
                                      </Box>
                                      <Box mb={3}>
                                        <PrimaryButton onClick={() => { 
                                          if (!fallbackFeedList.includes(fallbackFeedId)) {
                                            setFallbackFeedList([...fallbackFeedList, fallbackFeedId]);
                                            setFallbackListOptions(fallbackListOptions.filter((f) => f.value !== fallbackFeedId));
      
                                          }
                                        }}>
                                          Add
                                        </PrimaryButton>

                                      </Box>
                                    </Stack>
                                }

                            </Box>
                          ) : 
                            <Text variant='body' color='gray-lighter' sx={{ my:4 }}>
                              Not configured (using mbd default)
                            </Text>
                        }            
                          

                      <Box display="flex" alignItems="center" gap={2} mt={4}>
                        <Text variant='h2' > Cold Start User Configurations
                          <IconButton
                              ml={1}
                              onClick={() => {setOpenInfoDialog(true); 
                                setInfoDialogTitle(explanations.coldStart.title); 
                                setInfoDialogText(explanations.coldStart.description)
                              }}
                            >
                              <Tooltip title="What is cold start user configuration?">
                                <QuestionIcon sx={{color:colors['gray-lighter']}}/>
                              </Tooltip>
                          </IconButton>
                        </Text>
                        {
                          isEditMode &&
                          <Button onClick={() => {
                            setShowColdStartSection(!showColdStartSection);
                            if (!showColdStartSection) {
                                // Reset promotion fields when closing
                                setColdStartFeedId('');
                                setColdStartFeedName('');
                            }
                            }}>
                            <Tooltip title="Add or remove cold start user configurations">
                              <EditIcon sx={{color:primaryColor}}/>
                            </Tooltip>
                          </Button>
                        }
                      </Box>
                      {showColdStartSection ? (
                        <Box>
                          <Box mt={4}>
                              <Box display="flex" flexDirection={'row'} mt={4}>
                                <Box width={'100%'}>
                                  <Text variant='fieldHeading' color='gray-light'>
                                    Select Feed for Cold Start User
                                  </Text>
                                  <FeedSelector feeds={privateFeeds.filter((f) => f.endpoint === endpoint)} templateFeeds={publicFeeds.filter((f) => f.endpoint === endpoint)} filterOutFeedIds={[feedId]} selectedFeedId={coldStartFeedId} onChange={(newValue) => {
                                    if (newValue){
                                      setColdStartFeedId(newValue.config_id);
                                      setColdStartFeedName(newValue.name);
                                    }
                                    else{
                                      setColdStartFeedId('');
                                      setColdStartFeedName('');
                                    }
                                  }}/>
                                </Box>
                              </Box>
                          </Box>
                          {
                            coldStartFeedId !== '' &&
                            <Box>
                              <Stack direction='row' spacing={2} alignItems={'center'} mt={2}>
                                <CountField horizontal heading="Cold Start Threshold" minWidth={220} value={coldStartThreshold <= 0 ? '' : coldStartThreshold} onChange={(e) => setColdStartThreshold(e.target.value)} showExplanation={true} explanationTitle={explanations.coldStartThreshold.title} explanationDescription={explanations.impressionCount.description}/>
                                <Text>days</Text>
                              </Stack>
                              <Box mt={2}>
                                <CountField horizontal heading="Minimum Interaction Count" minWidth={220} value={coldStartMinimumInteractionCount <= 0 ? '' : coldStartMinimumInteractionCount} onChange={(e) => setColdStartMinimumInteractionCount(e.target.value)} showExplanation={true} explanationTitle={explanations.minimumInteractionCount.title} explanationDescription={explanations.minimumInteractionCount.description}/>
                              </Box>
                            </Box>

                          }


                        </Box>
                      ) : 
                        <Text variant='body' color='gray-lighter' sx={{ my:4 }}>
                          Not configured
                        </Text>
                      }
                      {/* Promotion Section */}
                      <Box display="flex" alignItems="center" gap={2} mt={4}>
                        
                        <Text variant='h2'>
                          Promotions
                          <IconButton
                                ml={1}
                                onClick={() => {setOpenInfoDialog(true); 
                                  setInfoDialogTitle(explanations.promotion.title); 
                                  setInfoDialogText(explanations.promotion.description)
                                }}
                              >
                                <Tooltip title="What is promotion?">
                                  <QuestionIcon sx={{color:colors['gray-lighter']}}/>
                                </Tooltip>
                              </IconButton>
                        </Text>
                        {/* {
                          isEditMode &&
                          <Button onClick={() => {
                            setShowPromotionSection(!showPromotionSection);
                            if (!showPromotionSection) {
                                // Reset promotion fields when closing
                                setPromotionName('');
                                setSelectedPromotionFeedId('');
                                setSelectedPromotionFeedName('');
                                setPromotionPercentage(50);
                                setItemsList([{ item_id: '', rank: '' }]);
                            }
                            }}>
                            <Tooltip title="Add or remove a promotion">
                              <EditIcon sx={{color:primaryColor}}/>
                            </Tooltip>
                          </Button>
                        } */}
                      </Box>

                      {true ? (
                        <>
                        <PromotionListDisplay />
                        <Box ref={promotionRef}></Box>
                        {
                          showAddPromotionSection &&
                          <Box mt={4} p={2} border={1} borderColor={colors['gray-lightest']} borderRadius={2}>
                            {
                              promotionEditIndex === -1 && promotionList.length >= MAX_FEED_PROMOTIONS && 
                              <Box my={2}> 
                              <Text variant='warning' color='warning'>
                                {`You have already reached the maximum of ${MAX_FEED_PROMOTIONS} feed promotions.`}
                              </Text>
                              </Box>
                            }
                            {
                              promotionEditIndex === -1 &&
                              <Stack direction='row' spacing={1} alignItems={'center'}>
                                <Radio
                                  disabled={promotionList.length >= MAX_FEED_PROMOTIONS}
                                  checked={promotionTypeValue === 'feed'}
                                  onChange={e => setPromotionTypeValue(e.target.value)}
                                  value="feed"
                                  name="radio-buttons"
                                  inputProps={{ 'aria-label': 'feed' }}
                                />
                                <Text>Feed</Text>
                                <Radio
                                  checked={promotionTypeValue === 'items'}
                                  onChange={e => setPromotionTypeValue(e.target.value)}
                                  value="items"
                                  name="radio-buttons"
                                  inputProps={{ 'aria-label': 'items' }}
                                />
                                <Text>Items</Text>
                              </Stack>
                            }

                            <Box gap={2} my={2}>

                              {/* Render fields for "feed" promotion type */}
                              {promotionTypeValue === 'feed' && (
                                <>
                                  <Box mt={4}>
                                    <EditableTextField horizontal={false} heading="Promotion Name (required)" value={promotionName} onChange={(e) => setPromotionName(e.target.value)}/>
                                    <Box mt={2} width={'100%'}>
                                      <Text variant='fieldHeading' color='brand-light'>
                                        Select Feed for Promotion (required)
                                      </Text>
                                      <FeedSelector feeds={privateFeeds.filter((f) => f.endpoint === endpoint)} templateFeeds={publicFeeds.filter((f) => f.endpoint === endpoint)} filterOutFeedIds={[feedId]} selectedFeedId={selectedPromotionFeedId} onChange={(newValue) => {
                                        setSelectedPromotionFeedId(newValue.config_id);
                                        setSelectedPromotionFeedName(newValue.name);
                                      }}/>
                                    </Box>
                                  </Box>
{/* 
                                  <Box display="flex" gap={2} my={2}>
                                    <NonEditableTextField horizontal={true} heading="Feed ID" value={selectedPromotionFeedId}/>
                                    <NonEditableTextField horizontal={true} heading="Feed Name" value={selectedPromotionFeedName}/>
                                  </Box> */}

                                  <Box mt={4} mb={4}>
                                    <NonEditableTextField horizontal={true} heading="Percentage" value={promotionPercentage +'%'}/>
                                    <Box mt={2} px={2}>
                                    <Slider
                                      value={promotionPercentage}
                                      onChange={(e, value) => setPromotionPercentage(value)}
                                      step={1}
                                      min={1}
                                      max={100}
                                      valueLabelDisplay="auto"
                                    />

                                    {
                                      invalidPercentage &&
                                      <Text variant='warning' color='warning'>Total percentage across all promotions is more than 100%</Text>
                                    }
                                    </Box>
                                  </Box>
                                  <Box mt={2} mb={4}>
                                  <NonEditableTextField horizontal={true} heading="Skew" value={skew}/>
                                  <Box mt={2} px={2}>
                                    <Slider
                                      value={skew}
                                      onChange={(e, value) => setSkew(value)}
                                      step={0.1}
                                      min={-1}
                                      max={1}
                                      valueLabelDisplay="auto"
                                    />
                                  </Box>
                                </Box>
                                <Stack direction='row' spacing={2} alignItems={'center'} justifyContent={'flex-end'} mt={2}>
                                  <SecondaryButton onClick={() => { setPromotionEditIndex(-1); setShowAddPromotionSection(false)}}>Cancel</SecondaryButton>
                                  <PrimaryButton onClick={() => handleSavePromotion()} disabled={invalidPercentage || promotionName === '' || selectedPromotionFeedId === '' || (promotionEditIndex === -1 && promotionList.length >= MAX_FEED_PROMOTIONS)}>Save</PrimaryButton>
                                </Stack>
                                </>
                              )}

                              {/* Render fields for "items" promotion type */}
                              {promotionTypeValue === 'items' && (
                                <>
                                  {itemsList.map((item, index) => (
                                    <Box display="flex" gap={2} my={2} key={index} alignItems={'center'} alignContent={'center'}>
                                      <Box flexGrow={1}>
                                        <Box sx={{display:'flex', flexDirection: 'row', alignItems:'center' }} >
                                          <Text variant='fieldHeading' color='brand-light'>
                                            Item ID
                                          </Text>
                                          <TextField 
                                            fullWidth
                                            value={item.item_id} 
                                            onChange={(e) => handleItemChange(e.target.value, index, 'item_id')} 
                                            variant="outlined" 
                                            sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: primaryColor} , marginLeft: 1}} 
                                          />

                                        </Box>                                
                                      </Box>
                                      <Box>
                                        <Box sx={{display:'flex', flexDirection: 'row', alignItems:'center' }} >
                                          <Text variant='fieldHeading' color='brand-light'>
                                            Rank
                                          </Text>
                                          <TextField 
                                            type="number"
                                            step={1}
                                            value={item.rank} 
                                            onChange={(e) => handleItemChange(e.target.value, index, 'rank')} 
                                            variant="outlined" 
                                            sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: primaryColor} , marginLeft: 1}} 
                                            inputProps={{
                                              min: 0,
                                              style: { textAlign: 'center' } // Center align the input text
                                            }}
                                          />
                                        </Box>                                
                                      </Box>

                                      <Box sx={{height: '100%', alignItems: 'center'}} py={2}>
                                        <IconButton mt={2} onClick={() => removeItem(index)} sx={{width: 40, marginRight: 1}}>
                                            <RemoveIcon />
                                        </IconButton>
                                      </Box>

                                    </Box>
                                  ))}
                                  <Stack direction={'row'} spacing={2} my={4} justifyContent={'flex-end'}>
                                    <SecondaryButton onClick={() => {
                                      setItemsList(itemsList.filter((item) => item.item_id !== '' && item.rank !== ''));
                                      setShowAddPromotionSection(false)
                                    }}>Done</SecondaryButton>
                                    <PrimaryButton onClick={addItem}> + Add Item </PrimaryButton> 
                                  </Stack>
                                </>
                              )}
                            </Box>
                          </Box>

                        }
                        </>
                      ) : 
                        <Text variant='body' color='gray-lighter' sx={{ my:4 }}>
                          Not configured
                        </Text>
                      }
                    </Card>

                  </>
              }

              {/* <Box display="flex" gap={2}>

                {!isMobile && !isPreviewOpen &&
                <Button
                  onClick={togglePreviewDrawer}
                  // startIcon={<PreviewIcon stye={{color: 'white'}}/>}
                  sx={{ position: 'fixed', width: 60, height: 60, borderRadius: '50%', right:40, bottom: 40,  backgroundColor: primaryColor, '&:hover': { backgroundColor: '#ed9ed2' }}}
                  
                >
                  <PreviewIcon sx={{color: 'white'}}/>
                </Button>}
              </Box> */}

              <Snackbar open={showSnackbar} 
              autoHideDuration={15000} 
              onClose={() => setShowSnackbar(false)}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              >
                <Alert onClose={() => setShowSnackbar(false)} severity="success" sx={{ width: '100%' }}>
                  {snackbarMessage}
                </Alert>
              </Snackbar>

            </Box>
          }

        </>
      )}
    </Grid>
    { !isShowcaseMode &&
    <Grid item xs={12} md={6} sm={4} sx={{borderLeftWidth: 1, borderLeftColor: '#e0e0e0'}}>
        <>
        {isMobile ? (
              <Box
                sx={{
                  width: '100%',
                  mt: 3, 
                  position: 'relative', 
                  bottom: 0,
                  display: feedId ? 'block' : 'none', 
                }}
              >
                <Typography variant="h6">Feed Preview</Typography>
                <Box
                  sx={{
                    mt: 2,
                    overflowY: 'auto',
                    height: '100%', 
                  }}
                >
                  {feedId ? (
                    <iframe
                      key={iframeKey}
                      src={`${playgroundUrl}/Feed_Preview?feed-id=${feedId}`}
                      width="100%" // Full width
                      height="100%" // Full height within the container
                      style={{ border: 'none' }}
                      title="Feed Preview"
                    ></iframe>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Please select a feed.
                    </Typography>
                  )}
                </Box>
              </Box>
            ) : (
              // Desktop view: render the drawer as usual
              // <Drawer
              //   anchor="right"
              //   open={isPreviewOpen}
              //   onClose={togglePreviewDrawer}
              //   PaperProps={{
              //     sx: {
              //       width: '600px',
              //       height: '100%',
              //       overflow: 'hidden',
              //     },
              //   }}
              // >
              <Box mt={1} height='100%'>
                <Card fullHeight title='Feed Preview' controls={                    
                  <SecondaryButton 
                    startIcon={<CodeIcon/>}  
                    onClick={ () => {
                      setCodeString(getCodeString(authContext.user?.farcaster_profile?.fid, feedId, endpoint, authContext.user.api_key, false ));
                      setShowCodeDialog(true);
                      Event('feed_builder_see_code');
                    }}> 
                    See Code
                  </SecondaryButton>
                }>
                  <Box p={0} sx={{ height: '100%', width: '100%', overflowY: 'hidden' }}>

                      {feedId ? (
                        <iframe
                          key={iframeKey}
                          src={`${playgroundUrl}/Feed_Preview?feed-id=${feedId}`}
                          width="100%"
                          height='96%'
                          style={{ border: 'none', minHeight: 'calc(100vh - 120px)' }}
                          title="Feed Preview"
                        ></iframe>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Please select a feed.
                        </Typography>
                      )}
                  </Box>
                </Card>
              </Box>
              // </Drawer>
            )}
        
        </>

      </Grid>    
    }

    </Grid>

    </Box>
  );
}
